import React, { useState, useEffect, useCallback } from 'react';
import { fetchIndicators, fetchSystemStatus, fetchHistorical, fetchStrategies, fetchTrades, fetchBacktest, fetchBacktestRange, fetchBacktestStraddle100, fetchBacktestStraddle100Range, fetchBacktestThetaExpiry, fetchBacktestThetaExpiryRange, fetchBacktestStraddle50Expiry, fetchBacktestStraddle50ExpiryRange, fetchBacktestThetaSensexWed, fetchBacktestThetaSensexWedRange, fetchBacktestStraddle300SensexWed, fetchBacktestStraddle300SensexWedRange, fetchBacktestThetaSensexExpiry, fetchBacktestThetaSensexExpiryRange, fetchBacktestStraddle100SensexExpiry, fetchBacktestStraddle100SensexExpiryRange, fetchAccounts, createAccount, updateAccount, deleteAccount, testAccountConnection, setDataFeedAccount, getAngelOneLoginUrl, getKiteLoginUrl, fetchStrategyConfigs, createStrategyConfig, updateStrategyConfig, deleteStrategyConfig } from './api';
import './App.css';

const REFRESH_INTERVAL = 15000;

const SIGNAL_CONFIG = {
  BULLISH: { label: 'BULLISH', color: '#22c55e', bg: 'rgba(34,197,94,0.12)', icon: '▲' },
  BEARISH: { label: 'BEARISH', color: '#ef4444', bg: 'rgba(239,68,68,0.12)', icon: '▼' },
  NEUTRAL: { label: 'NEUTRAL', color: '#a1a1aa', bg: 'rgba(161,161,170,0.10)', icon: '●' },
  NO_TRADE: { label: 'NO TRADE', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', icon: '⛔' },
  EXIT_TRADE: { label: 'EXIT', color: '#f97316', bg: 'rgba(249,115,22,0.12)', icon: '⚠' },
  OK: { label: 'OK', color: '#22c55e', bg: 'rgba(34,197,94,0.08)', icon: '✓' },
  WAITING: { label: 'WAITING', color: '#6b7280', bg: 'rgba(107,114,128,0.10)', icon: '⏳' },
};

function SignalBadge({ signal }) {
  const config = SIGNAL_CONFIG[signal] || SIGNAL_CONFIG.WAITING;
  return (
    <span
      className="signal-badge"
      style={{ color: config.color, background: config.bg, borderColor: config.color }}
    >
      {config.icon} {config.label}
    </span>
  );
}

function PriceCard({ label, value, change, changeLabel }) {
  const isPositive = change >= 0;
  return (
    <div className="price-card">
      <div className="price-label">{label}</div>
      <div className="price-value">{value?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '—'}</div>
      {change !== undefined && change !== null && (
        <div className={`price-change ${isPositive ? 'positive' : 'negative'}`}>
          {isPositive ? '+' : ''}{change.toFixed(2)}{changeLabel || ''}
        </div>
      )}
    </div>
  );
}

function IndicatorRow({ indicator }) {
  return (
    <tr className="indicator-row">
      <td className="ind-name">{indicator.name}</td>
      <td className="ind-signal"><SignalBadge signal={indicator.signal} /></td>
      <td className="ind-value">{indicator.value}</td>
      <td className="ind-detail">{indicator.detail}</td>
    </tr>
  );
}

function IndicatorDashboard({ data, timeLabel }) {
  const niftyChange = data.nifty_price - (data.nifty_prev_close || 0);
  const niftyChangePct = data.nifty_prev_close > 0
    ? ((niftyChange / data.nifty_prev_close) * 100)
    : 0;

  return (
    <>
      {timeLabel && (
        <div className="time-label-banner">
          Showing indicators at: <strong>{timeLabel}</strong>
        </div>
      )}

      {/* Price Cards */}
      <section className="price-cards">
        <PriceCard
          label="NIFTY 50"
          value={data.nifty_price}
          change={niftyChangePct}
          changeLabel={`% (${niftyChange >= 0 ? '+' : ''}${niftyChange.toFixed(0)} pts)`}
        />
        <PriceCard
          label="India VIX"
          value={data.vix_value}
          change={data.vix_change_pct}
          changeLabel="% intraday"
        />
        <div className="price-card day-range">
          <div className="price-label">Day Range</div>
          <div className="range-values">
            <span>L: {data.nifty_low?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            <span>H: {data.nifty_high?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
          </div>
          <div className="price-change neutral">
            Range: {data.intraday_range?.toFixed(0)} pts
          </div>
        </div>
        <div className="price-card prev-day">
          <div className="price-label">Previous Day</div>
          <div className="range-values">
            <span>L: {data.prev_day_low?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            <span>H: {data.prev_day_high?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
          </div>
          <div className="price-change neutral">
            Close: {data.nifty_prev_close?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
          </div>
        </div>
      </section>

      {/* Overall Bias */}
      <section className="overall-bias-section">
        <div className="overall-card">
          <div className="overall-header">
            <h2>Overall Market Bias</h2>
            <SignalBadge signal={data.overall_bias} />
          </div>
          <div className="bias-stats">
            <div className="bias-stat bullish">
              <span className="bias-count">{data.bullish_count}</span>
              <span className="bias-label">Bullish</span>
            </div>
            <div className="bias-stat bearish">
              <span className="bias-count">{data.bearish_count}</span>
              <span className="bias-label">Bearish</span>
            </div>
            <div className={`trade-status ${data.trade_allowed ? 'allowed' : 'blocked'}`}>
              {data.trade_allowed ? '✅ TRADE ALLOWED' : '🚫 NO TRADE'}
            </div>
          </div>
        </div>
      </section>

      {/* Indicators Table */}
      <section className="indicators-section">
        <h2>Indicator Dashboard</h2>
        <div className="table-wrapper">
          <table className="indicators-table">
            <thead>
              <tr>
                <th>Indicator</th>
                <th>Signal</th>
                <th>Value</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {data.indicators?.map((ind, idx) => (
                <IndicatorRow key={idx} indicator={ind} />
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* DMA values summary */}
      <section className="dma-section">
        <h2>Moving Averages</h2>
        <div className="dma-cards">
          <div className="dma-card">
            <div className="dma-label">20 DMA (Daily)</div>
            <div className="dma-value">{data.daily_20dma?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
            <SignalBadge signal={data.daily_20dma_signal} />
          </div>
          <div className="dma-card">
            <div className="dma-label">20 DMA (5 Min)</div>
            <div className="dma-value">{data.fivemin_20dma?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
          </div>
          <div className="dma-card">
            <div className="dma-label">200 DMA (5 Min)</div>
            <div className="dma-value">{data.fivemin_200dma?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
            <SignalBadge signal={data.fivemin_dma_signal} />
          </div>
          <div className="dma-card">
            <div className="dma-label">Gap</div>
            <div className="dma-value">{data.gap_points > 0 ? '+' : ''}{data.gap_points?.toFixed(0)} pts</div>
            <SignalBadge signal={data.gap_signal} />
          </div>
        </div>
      </section>

      {/* First 15m candle */}
      {(data.first_15m_high > 0 || data.first_15m_low > 0) && (
        <section className="candle-section">
          <h2>First 15-Min Candle</h2>
          <div className="candle-info">
            <div className="candle-stat">
              <span className="candle-label">High</span>
              <span className="candle-value">{data.first_15m_high?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            </div>
            <div className="candle-stat">
              <span className="candle-label">Low</span>
              <span className="candle-value">{data.first_15m_low?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            </div>
            <div className="candle-stat">
              <span className="candle-label">Nifty</span>
              <span className="candle-value">{data.nifty_price?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            </div>
            <SignalBadge signal={data.first_15m_signal} />
          </div>
        </section>
      )}
    </>
  );
}

function StrategyInstance({ instance, accounts, onSave, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    start_time: instance.start_time || '09:20',
    exit_time: instance.exit_time || '14:40',
    strike_mode: instance.strike_mode || 'ATM',
    itm_points: instance.itm_points || 0,
    lots: instance.lots || 1,
    account_id: instance.account_id || '',
    is_active: instance.is_active !== false,
  });

  const handleSave = () => {
    onSave(instance.id, form);
    setEditing(false);
  };

  if (!editing) {
    return (
      <div className="instance-row">
        <span className="instance-account">{instance.account_name || 'Paper'}</span>
        <span className="instance-detail">Start: {instance.start_time || '09:20'}</span>
        <span className="instance-detail">Exit: {instance.exit_time || '14:40'}</span>
        <span className="instance-detail">{instance.strike_mode || 'ATM'}{instance.strike_mode === 'ITM' ? ` ${instance.itm_points}pts` : ''}</span>
        <span className="instance-detail">Lots: {instance.lots || 1}</span>
        <span className={`instance-status ${instance.is_active !== false ? 'active' : 'inactive'}`}>
          {instance.is_active !== false ? '● Active' : '○ Inactive'}
        </span>
        <div className="instance-actions">
          <button className="btn-sm" onClick={() => setEditing(true)}>Edit</button>
          <button className="btn-sm btn-danger" onClick={() => onDelete(instance.id)}>✕</button>
        </div>
      </div>
    );
  }

  return (
    <div className="instance-row instance-editing">
      <div className="instance-form-grid">
        <div className="form-field">
          <label>Account</label>
          <select value={form.account_id} onChange={e => setForm({...form, account_id: e.target.value ? parseInt(e.target.value) : ''})}>
            <option value="">Paper Trading</option>
            {accounts.map(acc => <option key={acc.id} value={acc.id}>{acc.name}</option>)}
          </select>
        </div>
        <div className="form-field">
          <label>Start Time</label>
          <input type="time" value={form.start_time} onChange={e => setForm({...form, start_time: e.target.value})} />
        </div>
        <div className="form-field">
          <label>Exit Time</label>
          <input type="time" value={form.exit_time} onChange={e => setForm({...form, exit_time: e.target.value})} />
        </div>
        <div className="form-field">
          <label>Strike Mode</label>
          <select value={form.strike_mode} onChange={e => setForm({...form, strike_mode: e.target.value})}>
            <option value="ATM">ATM</option>
            <option value="ITM">ITM</option>
          </select>
        </div>
        {form.strike_mode === 'ITM' && (
          <div className="form-field">
            <label>ITM Points</label>
            <input type="number" value={form.itm_points} onChange={e => setForm({...form, itm_points: parseInt(e.target.value) || 0})} min="0" step="50" />
          </div>
        )}
        <div className="form-field">
          <label>Lots</label>
          <input type="number" value={form.lots} onChange={e => setForm({...form, lots: parseInt(e.target.value) || 1})} min="1" />
        </div>
        <div className="form-field">
          <label>
            <input type="checkbox" checked={form.is_active} onChange={e => setForm({...form, is_active: e.target.checked})} />
            {' '}Active
          </label>
        </div>
      </div>
      <div className="instance-actions">
        <button className="btn-sm btn-primary" onClick={handleSave}>Save</button>
        <button className="btn-sm" onClick={() => setEditing(false)}>Cancel</button>
      </div>
    </div>
  );
}

function App() {
  const [tab, setTab] = useState('live');
  const [data, setData] = useState(null);
  const [systemStatus, setSystemStatus] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [cycleCount, setCycleCount] = useState(0);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Historical state
  const [histDate, setHistDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 1);
    while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
  });
  const [histData, setHistData] = useState(null);
  const [histLoading, setHistLoading] = useState(false);
  const [histError, setHistError] = useState(null);
  const [selectedSnap, setSelectedSnap] = useState(null);

  // Strategy state
  const [stratData, setStratData] = useState(null);
  const [tradeData, setTradeData] = useState(null);

  // Backtest state
  const [btFrom, setBtFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 1);
    while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
  });
  const [btTo, setBtTo] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 1);
    while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
  });
  const [btData, setBtData] = useState(null);
  const [btLoading, setBtLoading] = useState(false);
  const [btError, setBtError] = useState(null);
  const [btExpandedDay, setBtExpandedDay] = useState(null);

  // Straddle100 Backtest state
  const [s100From, setS100From] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 1);
    while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
  });
  const [s100To, setS100To] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 1);
    while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
  });
  const [s100Data, setS100Data] = useState(null);
  const [s100Loading, setS100Loading] = useState(false);
  const [s100Error, setS100Error] = useState(null);
  const [s100ExpandedDay, setS100ExpandedDay] = useState(null);

  // ThetaExpiry Backtest state
  const [teFrom, setTeFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 1);
    while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
  });
  const [teTo, setTeTo] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 1);
    while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
  });
  const [teData, setTeData] = useState(null);
  const [teLoading, setTeLoading] = useState(false);
  const [teError, setTeError] = useState(null);
  const [teExpandedDay, setTeExpandedDay] = useState(null);

  // Straddle50Expiry Backtest state
  const [s50From, setS50From] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 1);
    while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
  });
  const [s50To, setS50To] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 1);
    while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
  });
  const [s50Data, setS50Data] = useState(null);
  const [s50Loading, setS50Loading] = useState(false);
  const [s50Error, setS50Error] = useState(null);
  const [s50ExpandedDay, setS50ExpandedDay] = useState(null);

  // Theta Sensex Wed Backtest state
  const [tswFrom, setTswFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 1);
    while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
  });
  const [tswTo, setTswTo] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 1);
    while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
  });
  const [tswData, setTswData] = useState(null);
  const [tswLoading, setTswLoading] = useState(false);
  const [tswError, setTswError] = useState(null);
  const [tswExpandedDay, setTswExpandedDay] = useState(null);

  // Straddle300 Sensex Wed Backtest state
  const [s3swFrom, setS3swFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 1);
    while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
  });
  const [s3swTo, setS3swTo] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 1);
    while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
  });
  const [s3swData, setS3swData] = useState(null);
  const [s3swLoading, setS3swLoading] = useState(false);
  const [s3swError, setS3swError] = useState(null);
  const [s3swExpandedDay, setS3swExpandedDay] = useState(null);

  // Theta Sensex Expiry Backtest state
  const [tseFrom, setTseFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 1);
    while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
  });
  const [tseTo, setTseTo] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 1);
    while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
  });
  const [tseData, setTseData] = useState(null);
  const [tseLoading, setTseLoading] = useState(false);
  const [tseError, setTseError] = useState(null);
  const [tseExpandedDay, setTseExpandedDay] = useState(null);

  // Straddle100 Sensex Expiry Backtest state
  const [s1seFrom, setS1seFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 1);
    while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
  });
  const [s1seTo, setS1seTo] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 1);
    while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
  });
  const [s1seData, setS1seData] = useState(null);
  const [s1seLoading, setS1seLoading] = useState(false);
  const [s1seError, setS1seError] = useState(null);
  const [s1seExpandedDay, setS1seExpandedDay] = useState(null);

  // Settings state
  const [accounts, setAccounts] = useState([]);
  const [strategyConfigs, setStrategyConfigs] = useState([]);
  const [strategyTypes, setStrategyTypes] = useState([]);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsError, setSettingsError] = useState(null);
  const [editingAccount, setEditingAccount] = useState(null);
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [newAccount, setNewAccount] = useState({ api_key: '', client_id: '', password: '', mpin: '', totp_secret: '', paper_trading: true });
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionResult, setConnectionResult] = useState(null);
  const [addingStrategyType, setAddingStrategyType] = useState(null);
  const [newInstance, setNewInstance] = useState({ start_time: '09:20', exit_time: '14:40', strike_mode: 'ATM', itm_points: 0, lots: 1, account_id: '', is_active: true });

  const loadSettings = useCallback(async () => {
    setSettingsLoading(true);
    setSettingsError(null);
    try {
      const [accRes, cfgRes] = await Promise.allSettled([fetchAccounts(), fetchStrategyConfigs()]);
      if (accRes.status === 'fulfilled') setAccounts(accRes.value.data?.accounts || []);
      if (cfgRes.status === 'fulfilled') {
        setStrategyConfigs(cfgRes.value.data?.configs || []);
        setStrategyTypes(cfgRes.value.data?.strategy_types || []);
      }
    } catch (err) {
      setSettingsError('Failed to load settings');
    }
    setSettingsLoading(false);
  }, []);

  const handleSaveAccount = async () => {
    try {
      if (editingAccount) {
        await updateAccount(editingAccount.id, editingAccount);
        setEditingAccount(null);
      } else {
        // Use profile name if available, fallback to client_id
        const name = connectionResult?.profile?.name || newAccount.client_id;
        await createAccount({ ...newAccount, name });
        setNewAccount({ api_key: '', client_id: '', password: '', mpin: '', totp_secret: '', paper_trading: true });
        setShowAddAccount(false);
        setConnectionResult(null);
      }
      await loadSettings();
    } catch (err) {
      setSettingsError('Failed to save account');
    }
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    setConnectionResult(null);
    try {
      const res = await testAccountConnection(newAccount);
      setConnectionResult(res.data);
    } catch (err) {
      setConnectionResult({ status: 'error', message: 'Connection failed. Check your credentials.' });
    }
    setTestingConnection(false);
  };

  const handleAngelOneLogin = async () => {
    try {
      const res = await getAngelOneLoginUrl();
      if (res.data?.status === 'ok' && res.data?.login_url) {
        window.location.href = res.data.login_url;
      } else {
        setSettingsError(res.data?.message || 'Could not get login URL');
      }
    } catch (err) {
      setSettingsError('Failed to initiate AngelOne login');
    }
  };

  const handleKiteLogin = async () => {
    try {
      const res = await getKiteLoginUrl();
      if (res.data?.status === 'ok' && res.data?.login_url) {
        window.location.href = res.data.login_url;
      } else {
        setSettingsError(res.data?.message || 'Could not get Kite login URL. Set KITE_API_KEY in .env.');
      }
    } catch (err) {
      setSettingsError('Failed to initiate Kite login');
    }
  };

  const handleSetDataFeed = async (id) => {
    try {
      await setDataFeedAccount(id);
      await loadSettings();
    } catch (err) {
      setSettingsError('Failed to set data feed account');
    }
  };

  const handleDeleteAccount = async (id) => {
    if (!window.confirm('Delete this broker account?')) return;
    try {
      await deleteAccount(id);
      await loadSettings();
    } catch (err) {
      setSettingsError('Failed to delete account');
    }
  };

  const handleUpdateInstance = async (id, updates) => {
    try {
      await updateStrategyConfig(id, updates);
      await loadSettings();
    } catch (err) {
      setSettingsError('Failed to update strategy config');
    }
  };

  const handleDeleteInstance = async (id) => {
    if (!window.confirm('Delete this strategy instance?')) return;
    try {
      await deleteStrategyConfig(id);
      await loadSettings();
    } catch (err) {
      setSettingsError('Failed to delete strategy instance');
    }
  };

  const handleAddInstance = async (strategyType) => {
    try {
      await createStrategyConfig({
        strategy_name: strategyType,
        ...newInstance,
        account_id: newInstance.account_id || null,
      });
      setAddingStrategyType(null);
      setNewInstance({ start_time: '09:20', exit_time: '14:40', strike_mode: 'ATM', itm_points: 0, lots: 1, account_id: '', is_active: true });
      await loadSettings();
    } catch (err) {
      setSettingsError('Failed to add strategy instance');
    }
  };

  const loadData = useCallback(async () => {
    try {
      const [indRes, sysRes, stratRes, tradeRes] = await Promise.allSettled([
        fetchIndicators(),
        fetchSystemStatus(),
        fetchStrategies(),
        fetchTrades('', 30),
      ]);

      if (indRes.status === 'fulfilled' && indRes.value.data?.data) {
        setData(indRes.value.data.data);
        setLastUpdate(indRes.value.data.last_update);
        setCycleCount(indRes.value.data.cycle_count || 0);
        setError(null);
      } else if (indRes.status === 'fulfilled' && indRes.value.data?.status === 'waiting') {
        setError('Waiting for first data cycle...');
      }

      if (sysRes.status === 'fulfilled') {
        setSystemStatus(sysRes.value.data);
      }

      if (stratRes.status === 'fulfilled' && stratRes.value.data?.status === 'ok') {
        setStratData(stratRes.value.data);
      }

      if (tradeRes.status === 'fulfilled' && tradeRes.value.data?.status === 'ok') {
        setTradeData(tradeRes.value.data);
      }

      setLoading(false);
    } catch (err) {
      setError('Failed to connect to backend');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [loadData]);

  // Handle OAuth callback params from AngelOne publisher login
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authSuccess = params.get('auth_success');
    const authError = params.get('auth_error');
    if (authSuccess || authError) {
      // Clean URL params
      window.history.replaceState({}, '', window.location.pathname);
      // Switch to settings tab
      setTab('settings');
      if (authSuccess) {
        setSettingsError(null);
        setConnectionResult({ status: 'ok', message: `AngelOne account ${authSuccess} connected via OAuth!` });
      } else if (authError) {
        setSettingsError(`AngelOne login failed: ${authError}`);
      }
      loadSettings();
    }
  }, []);

  const loadHistorical = useCallback(async () => {
    setHistLoading(true);
    setHistError(null);
    setHistData(null);
    setSelectedSnap(null);
    try {
      const res = await fetchHistorical(histDate);
      if (res.data?.status === 'ok') {
        setHistData(res.data);
        // Select the last snapshot by default
        if (res.data.snapshots?.length > 0) {
          setSelectedSnap(res.data.snapshots[res.data.snapshots.length - 1]);
        }
      } else {
        setHistError(res.data?.message || 'Failed to load historical data');
      }
    } catch (err) {
      setHistError('Failed to fetch historical data');
    }
    setHistLoading(false);
  }, [histDate]);

  const loadBacktest = useCallback(async () => {
    setBtLoading(true);
    setBtError(null);
    setBtData(null);
    setBtExpandedDay(null);
    try {
      let res;
      if (btFrom === btTo) {
        // Single day — use single-date endpoint
        res = await fetchBacktest(btFrom);
        if (res.data?.status === 'ok') {
          // Wrap single-day result into range format for uniform rendering
          const s = res.data.summary;
          setBtData({
            status: 'ok', from_date: btFrom, to_date: btTo,
            lot_size: res.data.lot_size,
            aggregate: {
              trading_days: 1,
              winning_days: s.pnl_per_lot >= 0 ? 1 : 0,
              losing_days: s.pnl_per_lot < 0 ? 1 : 0,
              win_rate: s.pnl_per_lot >= 0 ? 100 : 0,
              total_trades: s.total_trades,
              total_adjustments: s.total_adjustments,
              pnl_per_lot: s.pnl_per_lot,
              pnl_total: s.pnl_total,
              avg_pnl_per_day: s.pnl_per_lot,
            },
            days: [{ date: res.data.date, status: 'ok', summary: s, trades: res.data.trades, events: res.data.events }],
          });
        } else {
          setBtError(res.data?.message || 'Backtest failed');
        }
      } else {
        // Multi-day range
        res = await fetchBacktestRange(btFrom, btTo);
        if (res.data?.status === 'ok') {
          setBtData(res.data);
        } else {
          setBtError(res.data?.message || 'Backtest failed');
        }
      }
    } catch (err) {
      setBtError('Failed to run backtest');
    }
    setBtLoading(false);
  }, [btFrom, btTo]);

  const loadBacktestS100 = useCallback(async () => {
    setS100Loading(true);
    setS100Error(null);
    setS100Data(null);
    setS100ExpandedDay(null);
    try {
      let res;
      if (s100From === s100To) {
        res = await fetchBacktestStraddle100(s100From);
        if (res.data?.status === 'ok') {
          const s = res.data.summary;
          setS100Data({
            status: 'ok', from_date: s100From, to_date: s100To,
            lot_size: res.data.lot_size,
            aggregate: {
              trading_days: 1,
              winning_days: s.pnl_per_lot >= 0 ? 1 : 0,
              losing_days: s.pnl_per_lot < 0 ? 1 : 0,
              win_rate: s.pnl_per_lot >= 0 ? 100 : 0,
              total_trades: s.total_trades,
              total_adjustments: s.total_adjustments,
              pnl_per_lot: s.pnl_per_lot,
              pnl_total: s.pnl_total,
              avg_pnl_per_day: s.pnl_per_lot,
            },
            days: [{ date: res.data.date, status: 'ok', summary: s, trades: res.data.trades, events: res.data.events }],
          });
        } else {
          setS100Error(res.data?.message || 'Backtest failed');
        }
      } else {
        res = await fetchBacktestStraddle100Range(s100From, s100To);
        if (res.data?.status === 'ok') {
          setS100Data(res.data);
        } else {
          setS100Error(res.data?.message || 'Backtest failed');
        }
      }
    } catch (err) {
      setS100Error('Failed to run backtest');
    }
    setS100Loading(false);
  }, [s100From, s100To]);

  const loadBacktestTE = useCallback(async () => {
    setTeLoading(true);
    setTeError(null);
    setTeData(null);
    setTeExpandedDay(null);
    try {
      let res;
      if (teFrom === teTo) {
        res = await fetchBacktestThetaExpiry(teFrom);
        if (res.data?.status === 'ok') {
          const s = res.data.summary;
          setTeData({
            status: 'ok', from_date: teFrom, to_date: teTo,
            lot_size: res.data.lot_size,
            aggregate: {
              trading_days: 1,
              winning_days: s.pnl_per_lot >= 0 ? 1 : 0,
              losing_days: s.pnl_per_lot < 0 ? 1 : 0,
              win_rate: s.pnl_per_lot >= 0 ? 100 : 0,
              total_trades: s.total_trades,
              total_adjustments: s.total_adjustments,
              pnl_per_lot: s.pnl_per_lot,
              pnl_total: s.pnl_total,
              avg_pnl_per_day: s.pnl_per_lot,
            },
            days: [{ date: res.data.date, status: 'ok', summary: s, trades: res.data.trades, events: res.data.events }],
          });
        } else {
          setTeError(res.data?.message || 'Backtest failed');
        }
      } else {
        res = await fetchBacktestThetaExpiryRange(teFrom, teTo);
        if (res.data?.status === 'ok') {
          setTeData(res.data);
        } else {
          setTeError(res.data?.message || 'Backtest failed');
        }
      }
    } catch (err) {
      setTeError('Failed to run backtest');
    }
    setTeLoading(false);
  }, [teFrom, teTo]);

  const loadBacktestS50 = useCallback(async () => {
    setS50Loading(true);
    setS50Error(null);
    setS50Data(null);
    setS50ExpandedDay(null);
    try {
      let res;
      if (s50From === s50To) {
        res = await fetchBacktestStraddle50Expiry(s50From);
        if (res.data?.status === 'ok') {
          const s = res.data.summary;
          setS50Data({
            status: 'ok', from_date: s50From, to_date: s50To,
            lot_size: res.data.lot_size,
            aggregate: {
              trading_days: 1,
              winning_days: s.pnl_per_lot >= 0 ? 1 : 0,
              losing_days: s.pnl_per_lot < 0 ? 1 : 0,
              win_rate: s.pnl_per_lot >= 0 ? 100 : 0,
              total_trades: s.total_trades,
              total_adjustments: s.total_adjustments,
              pnl_per_lot: s.pnl_per_lot,
              pnl_total: s.pnl_total,
              avg_pnl_per_day: s.pnl_per_lot,
            },
            days: [{ date: res.data.date, status: 'ok', summary: s, trades: res.data.trades, events: res.data.events }],
          });
        } else {
          setS50Error(res.data?.message || 'Backtest failed');
        }
      } else {
        res = await fetchBacktestStraddle50ExpiryRange(s50From, s50To);
        if (res.data?.status === 'ok') {
          setS50Data(res.data);
        } else {
          setS50Error(res.data?.message || 'Backtest failed');
        }
      }
    } catch (err) {
      setS50Error('Failed to run backtest');
    }
    setS50Loading(false);
  }, [s50From, s50To]);

  const loadBacktestTSW = useCallback(async () => {
    setTswLoading(true); setTswError(null); setTswData(null); setTswExpandedDay(null);
    try {
      let res;
      if (tswFrom === tswTo) {
        res = await fetchBacktestThetaSensexWed(tswFrom);
        if (res.data?.status === 'ok') {
          const s = res.data.summary;
          setTswData({ status: 'ok', from_date: tswFrom, to_date: tswTo, lot_size: res.data.lot_size, aggregate: { trading_days: 1, winning_days: s.pnl_per_lot >= 0 ? 1 : 0, losing_days: s.pnl_per_lot < 0 ? 1 : 0, win_rate: s.pnl_per_lot >= 0 ? 100 : 0, total_trades: s.total_trades, total_adjustments: s.total_adjustments, pnl_per_lot: s.pnl_per_lot, pnl_total: s.pnl_total, avg_pnl_per_day: s.pnl_per_lot }, days: [{ date: res.data.date, status: 'ok', summary: s, trades: res.data.trades, events: res.data.events }] });
        } else { setTswError(res.data?.message || 'Backtest failed'); }
      } else {
        res = await fetchBacktestThetaSensexWedRange(tswFrom, tswTo);
        if (res.data?.status === 'ok') { setTswData(res.data); } else { setTswError(res.data?.message || 'Backtest failed'); }
      }
    } catch (err) { setTswError('Failed to run backtest'); }
    setTswLoading(false);
  }, [tswFrom, tswTo]);

  const loadBacktestS3SW = useCallback(async () => {
    setS3swLoading(true); setS3swError(null); setS3swData(null); setS3swExpandedDay(null);
    try {
      let res;
      if (s3swFrom === s3swTo) {
        res = await fetchBacktestStraddle300SensexWed(s3swFrom);
        if (res.data?.status === 'ok') {
          const s = res.data.summary;
          setS3swData({ status: 'ok', from_date: s3swFrom, to_date: s3swTo, lot_size: res.data.lot_size, aggregate: { trading_days: 1, winning_days: s.pnl_per_lot >= 0 ? 1 : 0, losing_days: s.pnl_per_lot < 0 ? 1 : 0, win_rate: s.pnl_per_lot >= 0 ? 100 : 0, total_trades: s.total_trades, total_adjustments: s.total_adjustments, pnl_per_lot: s.pnl_per_lot, pnl_total: s.pnl_total, avg_pnl_per_day: s.pnl_per_lot }, days: [{ date: res.data.date, status: 'ok', summary: s, trades: res.data.trades, events: res.data.events }] });
        } else { setS3swError(res.data?.message || 'Backtest failed'); }
      } else {
        res = await fetchBacktestStraddle300SensexWedRange(s3swFrom, s3swTo);
        if (res.data?.status === 'ok') { setS3swData(res.data); } else { setS3swError(res.data?.message || 'Backtest failed'); }
      }
    } catch (err) { setS3swError('Failed to run backtest'); }
    setS3swLoading(false);
  }, [s3swFrom, s3swTo]);

  const loadBacktestTSE = useCallback(async () => {
    setTseLoading(true); setTseError(null); setTseData(null); setTseExpandedDay(null);
    try {
      let res;
      if (tseFrom === tseTo) {
        res = await fetchBacktestThetaSensexExpiry(tseFrom);
        if (res.data?.status === 'ok') {
          const s = res.data.summary;
          setTseData({ status: 'ok', from_date: tseFrom, to_date: tseTo, lot_size: res.data.lot_size, aggregate: { trading_days: 1, winning_days: s.pnl_per_lot >= 0 ? 1 : 0, losing_days: s.pnl_per_lot < 0 ? 1 : 0, win_rate: s.pnl_per_lot >= 0 ? 100 : 0, total_trades: s.total_trades, total_adjustments: s.total_adjustments, pnl_per_lot: s.pnl_per_lot, pnl_total: s.pnl_total, avg_pnl_per_day: s.pnl_per_lot }, days: [{ date: res.data.date, status: 'ok', summary: s, trades: res.data.trades, events: res.data.events }] });
        } else { setTseError(res.data?.message || 'Backtest failed'); }
      } else {
        res = await fetchBacktestThetaSensexExpiryRange(tseFrom, tseTo);
        if (res.data?.status === 'ok') { setTseData(res.data); } else { setTseError(res.data?.message || 'Backtest failed'); }
      }
    } catch (err) { setTseError('Failed to run backtest'); }
    setTseLoading(false);
  }, [tseFrom, tseTo]);

  const loadBacktestS1SE = useCallback(async () => {
    setS1seLoading(true); setS1seError(null); setS1seData(null); setS1seExpandedDay(null);
    try {
      let res;
      if (s1seFrom === s1seTo) {
        res = await fetchBacktestStraddle100SensexExpiry(s1seFrom);
        if (res.data?.status === 'ok') {
          const s = res.data.summary;
          setS1seData({ status: 'ok', from_date: s1seFrom, to_date: s1seTo, lot_size: res.data.lot_size, aggregate: { trading_days: 1, winning_days: s.pnl_per_lot >= 0 ? 1 : 0, losing_days: s.pnl_per_lot < 0 ? 1 : 0, win_rate: s.pnl_per_lot >= 0 ? 100 : 0, total_trades: s.total_trades, total_adjustments: s.total_adjustments, pnl_per_lot: s.pnl_per_lot, pnl_total: s.pnl_total, avg_pnl_per_day: s.pnl_per_lot }, days: [{ date: res.data.date, status: 'ok', summary: s, trades: res.data.trades, events: res.data.events }] });
        } else { setS1seError(res.data?.message || 'Backtest failed'); }
      } else {
        res = await fetchBacktestStraddle100SensexExpiryRange(s1seFrom, s1seTo);
        if (res.data?.status === 'ok') { setS1seData(res.data); } else { setS1seError(res.data?.message || 'Backtest failed'); }
      }
    } catch (err) { setS1seError('Failed to run backtest'); }
    setS1seLoading(false);
  }, [s1seFrom, s1seTo]);

  return (
    <div className="app">
      {/* Header */}
      <header className="header">
        <div className="header-left">
          <h1>OptionSelling</h1>
          <span className="subtitle">Nifty Indicator Dashboard</span>
        </div>
        <div className="header-tabs">
          <button className={`tab-btn ${tab === 'live' ? 'active' : ''}`} onClick={() => setTab('live')}>Live</button>
          <button className={`tab-btn ${tab === 'strategy' ? 'active' : ''}`} onClick={() => setTab('strategy')}>Strategy</button>
          <button className={`tab-btn ${tab === 'historical' ? 'active' : ''}`} onClick={() => setTab('historical')}>Historical</button>
          <button className={`tab-btn ${tab === 'settings' ? 'active' : ''}`} onClick={() => { setTab('settings'); loadSettings(); }}>Settings</button>
        </div>
        <div className="header-right">
          <div className="header-stat">
            <span className="stat-label">Cycle</span>
            <span className="stat-value">#{cycleCount}</span>
          </div>
          <div className="header-stat">
            <span className="stat-label">Market</span>
            <span className={`stat-value ${systemStatus?.market_hours ? 'live' : 'closed'}`}>
              {systemStatus?.market_hours ? '● LIVE' : '○ CLOSED'}
            </span>
          </div>
          <div className="header-stat">
            <span className="stat-label">Updated</span>
            <span className="stat-value">{lastUpdate || '—'}</span>
          </div>
        </div>
      </header>

      <main className="main">
        {/* ═══════ LIVE TAB ═══════ */}
        {tab === 'live' && (
          <>
            {loading && !data && (
              <div className="loading">
                <div className="spinner" />
                <p>Connecting to backend...</p>
              </div>
            )}

            {error && !data && (
              <div className="error-banner">{error}</div>
            )}

            {data && <IndicatorDashboard data={data} />}
          </>
        )}

        {/* ═══════ STRATEGY TAB ═══════ */}
        {tab === 'strategy' && (
          <div className="strategy-view">
            {/* Strategy Header */}
            {stratData && (
              <section className="strat-header-section">
                <div className="strat-mode-badge">
                  <span className={`mode-dot ${stratData.paper_trading ? 'paper' : 'live-mode'}`} />
                  {stratData.paper_trading ? 'PAPER TRADING' : 'LIVE TRADING'}
                </div>
                <div className="strat-expiry">
                  Expiry: <strong>{stratData.expiry || '—'}</strong>
                </div>
              </section>
            )}

            {/* Strategy Cards */}
            {stratData?.strategies?.map((strat) => (
              <div key={strat.name} className="strat-card">
                <div className="strat-card-header">
                  <div className="strat-name-row">
                    <h2>{strat.strategy_type || strat.name}</h2>
                    <span className="strat-account-badge">{strat.account_name || 'Paper'}</span>
                    <span className={`strat-state strat-state-${strat.state.toLowerCase()}`}>
                      {strat.state === 'ACTIVE' ? '● ' : strat.state === 'WAITING' ? '◐ ' : strat.state === 'COMPLETED' ? '✓ ' : '○ '}{strat.state}
                    </span>
                  </div>
                  <div className="strat-timing">
                    <span className="strat-time-label">Start: <strong>{strat.start_time || '09:20'}</strong></span>
                    <span className="strat-time-label">Exit: <strong>{strat.exit_time || '14:40'}</strong></span>
                  </div>
                  <div className={`strat-pnl ${strat.pnl >= 0 ? 'positive' : 'negative'}`}>
                    P&L: {strat.pnl >= 0 ? '+' : ''}{strat.pnl.toFixed(2)} pts/lot
                  </div>
                </div>

                {/* Strategy Parameters */}
                <div className="strat-params">
                  <div className="param-grid">
                    <div className="param-item">
                      <span className="param-label">Lots</span>
                      <span className="param-value">{strat.params.lots}</span>
                    </div>
                    <div className="param-item">
                      <span className="param-label">First 15m High</span>
                      <span className="param-value">{strat.params.first_15m_high?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '—'}</span>
                    </div>
                    <div className="param-item">
                      <span className="param-label">First 15m Low</span>
                      <span className="param-value">{strat.params.first_15m_low?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '—'}</span>
                    </div>
                    <div className="param-item">
                      <span className="param-label">Entry Strike</span>
                      <span className="param-value">{strat.params.entry_strike || '—'}</span>
                    </div>
                    <div className="param-item">
                      <span className="param-label">Adjust Every</span>
                      <span className="param-value">{strat.params.adjustment_points} pts</span>
                    </div>
                    <div className="param-item">
                      <span className="param-label">Last Adjust Nifty</span>
                      <span className="param-value">{strat.params.last_adjustment_nifty?.toLocaleString('en-IN', { minimumFractionDigits: 2 }) || '—'}</span>
                    </div>
                    <div className="param-item">
                      <span className="param-label">Closed Trades</span>
                      <span className="param-value">{strat.params.closed_trades}</span>
                    </div>
                  </div>
                </div>

                {/* Open Positions */}
                {strat.positions?.length > 0 && (
                  <div className="strat-positions">
                    <h3>Open Positions</h3>
                    <div className="table-wrapper">
                      <table className="strat-table">
                        <thead>
                          <tr>
                            <th>Strike</th>
                            <th>CE Symbol</th>
                            <th>CE Sell ₹</th>
                            <th>CE LTP ₹</th>
                            <th>PE Symbol</th>
                            <th>PE Sell ₹</th>
                            <th>PE LTP ₹</th>
                            <th>Entry</th>
                            <th>P&L</th>
                          </tr>
                        </thead>
                        <tbody>
                          {strat.positions.map((pos, i) => (
                            <tr key={i}>
                              <td className="strike-cell">{pos.strike}</td>
                              <td className="sym-cell">{pos.ce_symbol || '—'}</td>
                              <td>{pos.ce_sell_price?.toFixed(2)}</td>
                              <td>{pos.ce_current_price?.toFixed(2)}</td>
                              <td className="sym-cell">{pos.pe_symbol || '—'}</td>
                              <td>{pos.pe_sell_price?.toFixed(2)}</td>
                              <td>{pos.pe_current_price?.toFixed(2)}</td>
                              <td>{pos.entry_time}</td>
                              <td className={pos.pnl >= 0 ? 'positive' : 'negative'}>
                                {pos.pnl >= 0 ? '+' : ''}{pos.pnl?.toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Event Log */}
                {strat.events?.length > 0 && (
                  <div className="strat-events">
                    <h3>Event Log ({strat.events.length})</h3>
                    <div className="event-list">
                      {[...strat.events].reverse().map((evt, i) => (
                        <div key={i} className={`event-item event-${evt.type?.toLowerCase()}`}>
                          <span className="event-time">{evt.time}</span>
                          <span className={`event-type-badge type-${evt.type?.toLowerCase()}`}>{evt.type}</span>
                          <span className="event-msg">{evt.message}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}

            {/* ═══════ BACKTEST SECTION ═══════ */}
            <div className="bt-section">
              <div className="bt-header">
                <h2>📊 Strategy Backtest</h2>
                <div className="bt-controls">
                  <div className="bt-date-group">
                    <label>From</label>
                    <input type="date" className="bt-date-input" value={btFrom}
                      onChange={(e) => setBtFrom(e.target.value)}
                      max={new Date().toISOString().split('T')[0]} />
                  </div>
                  <div className="bt-date-group">
                    <label>To</label>
                    <input type="date" className="bt-date-input" value={btTo}
                      onChange={(e) => setBtTo(e.target.value)}
                      max={new Date().toISOString().split('T')[0]} />
                  </div>
                  <button className="bt-run-btn" onClick={loadBacktest} disabled={btLoading}>
                    {btLoading ? 'Running...' : 'Run Backtest'}
                  </button>
                  {btData && (
                    <button className="bt-export-btn" onClick={() => {
                      const rows = [['Date','Open','Close','Range','15m High','15m Low','Trades','Adjustments','P&L/Lot','P&L Total','Trade#','Strike','Entry Time','CE Sell','PE Sell','Exit Time','CE Buy','PE Buy','Exit Reason','CE P&L','PE P&L','Trade P&L']];
                      btData.days.forEach(day => {
                        if (day.status !== 'ok') {
                          rows.push([day.date,'','','','','','','','','',day.message || 'No data']);
                          return;
                        }
                        const s = day.summary;
                        if (day.trades?.length > 0) {
                          day.trades.forEach((t, i) => {
                            rows.push([
                              i === 0 ? day.date : '', i === 0 ? s.nifty_open : '', i === 0 ? s.nifty_close : '',
                              i === 0 ? s.nifty_range?.toFixed(0) : '', i === 0 ? s.first_15m_high?.toFixed(0) : '',
                              i === 0 ? s.first_15m_low?.toFixed(0) : '', i === 0 ? s.total_trades : '',
                              i === 0 ? s.total_adjustments : '', i === 0 ? s.pnl_per_lot?.toFixed(2) : '',
                              i === 0 ? s.pnl_total : '',
                              t.trade_no, t.strike, t.entry_time, t.ce_entry?.toFixed(2), t.pe_entry?.toFixed(2),
                              t.exit_time, t.ce_exit?.toFixed(2), t.pe_exit?.toFixed(2), t.exit_reason,
                              t.ce_pnl?.toFixed(2), t.pe_pnl?.toFixed(2), t.total_pnl?.toFixed(2)
                            ]);
                          });
                        } else {
                          rows.push([day.date, s.nifty_open, s.nifty_close, s.nifty_range?.toFixed(0),
                            s.first_15m_high?.toFixed(0), s.first_15m_low?.toFixed(0), s.total_trades,
                            s.total_adjustments, s.pnl_per_lot?.toFixed(2), s.pnl_total]);
                        }
                      });
                      // Add totals row
                      const a = btData.aggregate;
                      rows.push([]);
                      rows.push(['SUMMARY','Trading Days:',a.trading_days,'Win Rate:',a.win_rate+'%','Total Trades:',a.total_trades,'','Total P&L/Lot:',a.pnl_per_lot?.toFixed(2),'Total P&L:',a.pnl_total,'Avg P&L/Day:',a.avg_pnl_per_day?.toFixed(2)]);
                      const csv = rows.map(r => r.map(v => `"${v ?? ''}"`).join(',')).join('\n');
                      const blob = new Blob([csv], { type: 'text/csv' });
                      const url = URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = `backtest_${btData.from_date}_to_${btData.to_date}.csv`;
                      link.click();
                      URL.revokeObjectURL(url);
                    }}>
                      ⬇ Export CSV
                    </button>
                  )}
                </div>
              </div>

              {btLoading && (
                <div className="loading">
                  <div className="spinner" />
                  <p>Running backtest... Each day takes ~30s (1-min candle data).</p>
                </div>
              )}

              {btError && <div className="error-banner">{btError}</div>}

              {btData && (
                <div className="bt-results">
                  {/* Aggregate Summary */}
                  <div className="bt-summary">
                    <div className="bt-summary-card">
                      <span className="bt-label">Period</span>
                      <span className="bt-val">{btData.from_date} → {btData.to_date}</span>
                    </div>
                    <div className="bt-summary-card highlight">
                      <span className="bt-label">Trading Days</span>
                      <span className="bt-val big">{btData.aggregate.trading_days}</span>
                    </div>
                    <div className="bt-summary-card highlight pnl-pos">
                      <span className="bt-label">Winning Days</span>
                      <span className="bt-val big">{btData.aggregate.winning_days}</span>
                    </div>
                    <div className="bt-summary-card highlight pnl-neg">
                      <span className="bt-label">Losing Days</span>
                      <span className="bt-val big">{btData.aggregate.losing_days}</span>
                    </div>
                    <div className="bt-summary-card highlight">
                      <span className="bt-label">Win Rate</span>
                      <span className="bt-val big">{btData.aggregate.win_rate}%</span>
                    </div>
                    <div className="bt-summary-card highlight">
                      <span className="bt-label">Total Trades</span>
                      <span className="bt-val big">{btData.aggregate.total_trades}</span>
                    </div>
                    <div className={`bt-summary-card highlight ${btData.aggregate.pnl_per_lot >= 0 ? 'pnl-pos' : 'pnl-neg'}`}>
                      <span className="bt-label">Total P&L / Lot</span>
                      <span className="bt-val big">{btData.aggregate.pnl_per_lot >= 0 ? '+' : ''}{btData.aggregate.pnl_per_lot?.toFixed(2)}</span>
                    </div>
                    <div className={`bt-summary-card highlight ${btData.aggregate.pnl_total >= 0 ? 'pnl-pos' : 'pnl-neg'}`}>
                      <span className="bt-label">Total P&L (₹)</span>
                      <span className="bt-val big">₹{btData.aggregate.pnl_total >= 0 ? '+' : ''}{btData.aggregate.pnl_total?.toLocaleString('en-IN')}</span>
                    </div>
                    <div className={`bt-summary-card ${btData.aggregate.avg_pnl_per_day >= 0 ? 'pnl-pos' : 'pnl-neg'}`}>
                      <span className="bt-label">Avg P&L / Day</span>
                      <span className="bt-val">{btData.aggregate.avg_pnl_per_day >= 0 ? '+' : ''}{btData.aggregate.avg_pnl_per_day?.toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Per-Day Summary Table */}
                  <div className="bt-day-table">
                    <h3>Daily Breakdown</h3>
                    <div className="table-wrapper">
                      <table className="bt-trade-table">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Open</th>
                            <th>Close</th>
                            <th>Range</th>
                            <th>15m H</th>
                            <th>15m L</th>
                            <th>Trades</th>
                            <th>Adj</th>
                            <th>P&L/Lot</th>
                            <th>P&L (₹)</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {btData.days.map((day) => (
                            <React.Fragment key={day.date}>
                              <tr className={`day-row ${day.status !== 'ok' ? 'day-error' : day.summary?.pnl_per_lot >= 0 ? 'day-win' : 'day-loss'}`}
                                  onClick={() => setBtExpandedDay(btExpandedDay === day.date ? null : day.date)}
                                  style={{ cursor: 'pointer' }}>
                                <td><strong>{day.date}</strong></td>
                                {day.status === 'ok' ? (
                                  <>
                                    <td>{day.summary.nifty_open?.toLocaleString('en-IN')}</td>
                                    <td>{day.summary.nifty_close?.toLocaleString('en-IN')}</td>
                                    <td>{day.summary.nifty_range?.toFixed(0)}</td>
                                    <td>{day.summary.first_15m_high?.toFixed(0)}</td>
                                    <td>{day.summary.first_15m_low?.toFixed(0)}</td>
                                    <td>{day.summary.total_trades}</td>
                                    <td>{day.summary.total_adjustments}</td>
                                    <td className={day.summary.pnl_per_lot >= 0 ? 'positive' : 'negative'}>
                                      <strong>{day.summary.pnl_per_lot >= 0 ? '+' : ''}{day.summary.pnl_per_lot?.toFixed(2)}</strong>
                                    </td>
                                    <td className={day.summary.pnl_total >= 0 ? 'positive' : 'negative'}>
                                      ₹{day.summary.pnl_total >= 0 ? '+' : ''}{day.summary.pnl_total?.toLocaleString('en-IN')}
                                    </td>
                                    <td className="expand-icon">{btExpandedDay === day.date ? '▼' : '▶'}</td>
                                  </>
                                ) : (
                                  <>
                                    <td colSpan="8" className="day-error-msg">{day.message || 'No data'}</td>
                                    <td></td>
                                  </>
                                )}
                              </tr>

                              {/* Expanded trade details */}
                              {btExpandedDay === day.date && day.status === 'ok' && (
                                <tr className="expanded-row">
                                  <td colSpan="11">
                                    <div className="bt-expanded-detail">
                                      <table className="bt-trade-table inner-table">
                                        <thead>
                                          <tr>
                                            <th>#</th>
                                            <th>Strike</th>
                                            <th>Entry</th>
                                            <th>CE Sell ₹</th>
                                            <th>PE Sell ₹</th>
                                            <th>Exit</th>
                                            <th>CE Buy ₹</th>
                                            <th>PE Buy ₹</th>
                                            <th>Reason</th>
                                            <th>CE P&L</th>
                                            <th>PE P&L</th>
                                            <th>Total</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {day.trades.map((t, i) => (
                                            <tr key={i}>
                                              <td>{t.trade_no}</td>
                                              <td className="strike-cell">{t.strike}</td>
                                              <td className="time-cell">{t.entry_time}</td>
                                              <td>{t.ce_entry?.toFixed(2)}</td>
                                              <td>{t.pe_entry?.toFixed(2)}</td>
                                              <td className="time-cell">{t.exit_time}</td>
                                              <td>{t.ce_exit?.toFixed(2)}</td>
                                              <td>{t.pe_exit?.toFixed(2)}</td>
                                              <td className="reason-cell">{t.exit_reason}</td>
                                              <td className={t.ce_pnl >= 0 ? 'positive' : 'negative'}>
                                                {t.ce_pnl >= 0 ? '+' : ''}{t.ce_pnl?.toFixed(2)}
                                              </td>
                                              <td className={t.pe_pnl >= 0 ? 'positive' : 'negative'}>
                                                {t.pe_pnl >= 0 ? '+' : ''}{t.pe_pnl?.toFixed(2)}
                                              </td>
                                              <td className={`total-cell ${t.total_pnl >= 0 ? 'positive' : 'negative'}`}>
                                                {t.total_pnl >= 0 ? '+' : ''}{t.total_pnl?.toFixed(2)}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                          {btData.days.filter(d => d.status === 'ok').length > 1 && (
                            <tr className="total-row">
                              <td><strong>TOTAL</strong></td>
                              <td colSpan="6"></td>
                              <td></td>
                              <td className={`total-cell ${btData.aggregate.pnl_per_lot >= 0 ? 'positive' : 'negative'}`}>
                                <strong>{btData.aggregate.pnl_per_lot >= 0 ? '+' : ''}{btData.aggregate.pnl_per_lot?.toFixed(2)}</strong>
                              </td>
                              <td className={`total-cell ${btData.aggregate.pnl_total >= 0 ? 'positive' : 'negative'}`}>
                                <strong>₹{btData.aggregate.pnl_total >= 0 ? '+' : ''}{btData.aggregate.pnl_total?.toLocaleString('en-IN')}</strong>
                              </td>
                              <td></td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ═══════ STRADDLE100 BACKTEST SECTION ═══════ */}
            <div className="bt-section">
              <div className="bt-header">
                <h2>📊 Straddle100 Backtest</h2>
                <div className="bt-controls">
                  <div className="bt-date-group">
                    <label>From</label>
                    <input type="date" className="bt-date-input" value={s100From}
                      onChange={(e) => setS100From(e.target.value)}
                      max={new Date().toISOString().split('T')[0]} />
                  </div>
                  <div className="bt-date-group">
                    <label>To</label>
                    <input type="date" className="bt-date-input" value={s100To}
                      onChange={(e) => setS100To(e.target.value)}
                      max={new Date().toISOString().split('T')[0]} />
                  </div>
                  <button className="bt-run-btn" onClick={loadBacktestS100} disabled={s100Loading}>
                    {s100Loading ? 'Running...' : 'Run Backtest'}
                  </button>
                  {s100Data && (
                    <button className="bt-export-btn" onClick={() => {
                      const rows = [['Date','Open','Close','Range','Trades','Adjustments','P&L/Lot','P&L Total','Trade#','Strike','Entry Time','CE Sell','PE Sell','Exit Time','CE Buy','PE Buy','Exit Reason','CE P&L','PE P&L','Trade P&L']];
                      s100Data.days.forEach(day => {
                        if (day.status !== 'ok') {
                          rows.push([day.date,'','','','','','','',day.message || 'No data']);
                          return;
                        }
                        const s = day.summary;
                        if (day.trades?.length > 0) {
                          day.trades.forEach((t, i) => {
                            rows.push([
                              i === 0 ? day.date : '', i === 0 ? s.nifty_open : '', i === 0 ? s.nifty_close : '',
                              i === 0 ? s.nifty_range?.toFixed(0) : '', i === 0 ? s.total_trades : '',
                              i === 0 ? s.total_adjustments : '', i === 0 ? s.pnl_per_lot?.toFixed(2) : '',
                              i === 0 ? s.pnl_total : '',
                              t.trade_no, t.strike, t.entry_time, t.ce_entry?.toFixed(2), t.pe_entry?.toFixed(2),
                              t.exit_time, t.ce_exit?.toFixed(2), t.pe_exit?.toFixed(2), t.exit_reason,
                              t.ce_pnl?.toFixed(2), t.pe_pnl?.toFixed(2), t.total_pnl?.toFixed(2)
                            ]);
                          });
                        } else {
                          rows.push([day.date, s.nifty_open, s.nifty_close, s.nifty_range?.toFixed(0), s.total_trades,
                            s.total_adjustments, s.pnl_per_lot?.toFixed(2), s.pnl_total]);
                        }
                      });
                      const a = s100Data.aggregate;
                      rows.push([]);
                      rows.push(['SUMMARY','Trading Days:',a.trading_days,'Win Rate:',a.win_rate+'%','Total Trades:',a.total_trades,'Total P&L/Lot:',a.pnl_per_lot?.toFixed(2),'Total P&L:',a.pnl_total,'Avg P&L/Day:',a.avg_pnl_per_day?.toFixed(2)]);
                      const csv = rows.map(r => r.map(v => `"${v ?? ''}"`).join(',')).join('\n');
                      const blob = new Blob([csv], { type: 'text/csv' });
                      const url = URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = `straddle100_${s100Data.from_date}_to_${s100Data.to_date}.csv`;
                      link.click();
                      URL.revokeObjectURL(url);
                    }}>
                      ⬇ Export CSV
                    </button>
                  )}
                </div>
              </div>

              {s100Loading && (
                <div className="loading">
                  <div className="spinner" />
                  <p>Running Straddle100 backtest... Each day takes ~30s.</p>
                </div>
              )}

              {s100Error && <div className="error-banner">{s100Error}</div>}

              {s100Data && (
                <div className="bt-results">
                  <div className="bt-summary">
                    <div className="bt-summary-card">
                      <span className="bt-label">Period</span>
                      <span className="bt-val">{s100Data.from_date} → {s100Data.to_date}</span>
                    </div>
                    <div className="bt-summary-card highlight">
                      <span className="bt-label">Trading Days</span>
                      <span className="bt-val big">{s100Data.aggregate.trading_days}</span>
                    </div>
                    <div className="bt-summary-card highlight pnl-pos">
                      <span className="bt-label">Winning Days</span>
                      <span className="bt-val big">{s100Data.aggregate.winning_days}</span>
                    </div>
                    <div className="bt-summary-card highlight pnl-neg">
                      <span className="bt-label">Losing Days</span>
                      <span className="bt-val big">{s100Data.aggregate.losing_days}</span>
                    </div>
                    <div className="bt-summary-card highlight">
                      <span className="bt-label">Win Rate</span>
                      <span className="bt-val big">{s100Data.aggregate.win_rate}%</span>
                    </div>
                    <div className="bt-summary-card highlight">
                      <span className="bt-label">Total Trades</span>
                      <span className="bt-val big">{s100Data.aggregate.total_trades}</span>
                    </div>
                    <div className={`bt-summary-card highlight ${s100Data.aggregate.pnl_per_lot >= 0 ? 'pnl-pos' : 'pnl-neg'}`}>
                      <span className="bt-label">Total P&L / Lot</span>
                      <span className="bt-val big">{s100Data.aggregate.pnl_per_lot >= 0 ? '+' : ''}{s100Data.aggregate.pnl_per_lot?.toFixed(2)}</span>
                    </div>
                    <div className={`bt-summary-card highlight ${s100Data.aggregate.pnl_total >= 0 ? 'pnl-pos' : 'pnl-neg'}`}>
                      <span className="bt-label">Total P&L (₹)</span>
                      <span className="bt-val big">₹{s100Data.aggregate.pnl_total >= 0 ? '+' : ''}{s100Data.aggregate.pnl_total?.toLocaleString('en-IN')}</span>
                    </div>
                    <div className={`bt-summary-card ${s100Data.aggregate.avg_pnl_per_day >= 0 ? 'pnl-pos' : 'pnl-neg'}`}>
                      <span className="bt-label">Avg P&L / Day</span>
                      <span className="bt-val">{s100Data.aggregate.avg_pnl_per_day >= 0 ? '+' : ''}{s100Data.aggregate.avg_pnl_per_day?.toFixed(2)}</span>
                    </div>
                  </div>

                  <div className="bt-day-table">
                    <h3>Daily Breakdown</h3>
                    <div className="table-wrapper">
                      <table className="bt-trade-table">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Open</th>
                            <th>Close</th>
                            <th>Range</th>
                            <th>Trades</th>
                            <th>Adj</th>
                            <th>P&L/Lot</th>
                            <th>P&L (₹)</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {s100Data.days.map((day) => (
                            <React.Fragment key={day.date}>
                              <tr className={`day-row ${day.status !== 'ok' ? 'day-error' : day.summary?.pnl_per_lot >= 0 ? 'day-win' : 'day-loss'}`}
                                  onClick={() => setS100ExpandedDay(s100ExpandedDay === day.date ? null : day.date)}
                                  style={{ cursor: 'pointer' }}>
                                <td><strong>{day.date}</strong></td>
                                {day.status === 'ok' ? (
                                  <>
                                    <td>{day.summary.nifty_open?.toLocaleString('en-IN')}</td>
                                    <td>{day.summary.nifty_close?.toLocaleString('en-IN')}</td>
                                    <td>{day.summary.nifty_range?.toFixed(0)}</td>
                                    <td>{day.summary.total_trades}</td>
                                    <td>{day.summary.total_adjustments}</td>
                                    <td className={day.summary.pnl_per_lot >= 0 ? 'positive' : 'negative'}>
                                      <strong>{day.summary.pnl_per_lot >= 0 ? '+' : ''}{day.summary.pnl_per_lot?.toFixed(2)}</strong>
                                    </td>
                                    <td className={day.summary.pnl_total >= 0 ? 'positive' : 'negative'}>
                                      ₹{day.summary.pnl_total >= 0 ? '+' : ''}{day.summary.pnl_total?.toLocaleString('en-IN')}
                                    </td>
                                    <td className="expand-icon">{s100ExpandedDay === day.date ? '▼' : '▶'}</td>
                                  </>
                                ) : (
                                  <>
                                    <td colSpan="6" className="day-error-msg">{day.message || 'No data'}</td>
                                    <td></td>
                                  </>
                                )}
                              </tr>

                              {s100ExpandedDay === day.date && day.status === 'ok' && (
                                <tr className="expanded-row">
                                  <td colSpan="9">
                                    <div className="bt-expanded-detail">
                                      <table className="bt-trade-table inner-table">
                                        <thead>
                                          <tr>
                                            <th>#</th>
                                            <th>Strike</th>
                                            <th>Entry</th>
                                            <th>CE Sell ₹</th>
                                            <th>PE Sell ₹</th>
                                            <th>Exit</th>
                                            <th>CE Buy ₹</th>
                                            <th>PE Buy ₹</th>
                                            <th>Reason</th>
                                            <th>CE P&L</th>
                                            <th>PE P&L</th>
                                            <th>Total</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {day.trades.map((t, i) => (
                                            <tr key={i}>
                                              <td>{t.trade_no}</td>
                                              <td className="strike-cell">{t.strike}</td>
                                              <td className="time-cell">{t.entry_time}</td>
                                              <td>{t.ce_entry?.toFixed(2)}</td>
                                              <td>{t.pe_entry?.toFixed(2)}</td>
                                              <td className="time-cell">{t.exit_time}</td>
                                              <td>{t.ce_exit?.toFixed(2)}</td>
                                              <td>{t.pe_exit?.toFixed(2)}</td>
                                              <td className="reason-cell">{t.exit_reason}</td>
                                              <td className={t.ce_pnl >= 0 ? 'positive' : 'negative'}>
                                                {t.ce_pnl >= 0 ? '+' : ''}{t.ce_pnl?.toFixed(2)}
                                              </td>
                                              <td className={t.pe_pnl >= 0 ? 'positive' : 'negative'}>
                                                {t.pe_pnl >= 0 ? '+' : ''}{t.pe_pnl?.toFixed(2)}
                                              </td>
                                              <td className={`total-cell ${t.total_pnl >= 0 ? 'positive' : 'negative'}`}>
                                                {t.total_pnl >= 0 ? '+' : ''}{t.total_pnl?.toFixed(2)}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                          {s100Data.days.filter(d => d.status === 'ok').length > 1 && (
                            <tr className="total-row">
                              <td><strong>TOTAL</strong></td>
                              <td colSpan="4"></td>
                              <td></td>
                              <td className={`total-cell ${s100Data.aggregate.pnl_per_lot >= 0 ? 'positive' : 'negative'}`}>
                                <strong>{s100Data.aggregate.pnl_per_lot >= 0 ? '+' : ''}{s100Data.aggregate.pnl_per_lot?.toFixed(2)}</strong>
                              </td>
                              <td className={`total-cell ${s100Data.aggregate.pnl_total >= 0 ? 'positive' : 'negative'}`}>
                                <strong>₹{s100Data.aggregate.pnl_total >= 0 ? '+' : ''}{s100Data.aggregate.pnl_total?.toLocaleString('en-IN')}</strong>
                              </td>
                              <td></td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ═══════ THETA EXPIRY BACKTEST SECTION ═══════ */}
            <div className="bt-section">
              <div className="bt-header">
                <h2>📊 Theta Expiry Backtest (Tuesday, 50pt)</h2>
                <div className="bt-controls">
                  <div className="bt-date-group">
                    <label>From</label>
                    <input type="date" className="bt-date-input" value={teFrom}
                      onChange={(e) => setTeFrom(e.target.value)}
                      max={new Date().toISOString().split('T')[0]} />
                  </div>
                  <div className="bt-date-group">
                    <label>To</label>
                    <input type="date" className="bt-date-input" value={teTo}
                      onChange={(e) => setTeTo(e.target.value)}
                      max={new Date().toISOString().split('T')[0]} />
                  </div>
                  <button className="bt-run-btn" onClick={loadBacktestTE} disabled={teLoading}>
                    {teLoading ? 'Running...' : 'Run Backtest'}
                  </button>
                  {teData && (
                    <button className="bt-export-btn" onClick={() => {
                      const rows = [['Date','Open','Close','Range','Trades','Adjustments','P&L/Lot','P&L Total','Trade#','Strike','Entry Time','CE Sell','PE Sell','Exit Time','CE Buy','PE Buy','Exit Reason','CE P&L','PE P&L','Trade P&L']];
                      teData.days.forEach(day => {
                        if (day.status !== 'ok') { rows.push([day.date,'','','','','','','',day.message || 'No data']); return; }
                        const s = day.summary;
                        if (day.trades?.length > 0) {
                          day.trades.forEach((t, i) => {
                            rows.push([i === 0 ? day.date : '', i === 0 ? s.nifty_open : '', i === 0 ? s.nifty_close : '',
                              i === 0 ? s.nifty_range?.toFixed(0) : '', i === 0 ? s.total_trades : '',
                              i === 0 ? s.total_adjustments : '', i === 0 ? s.pnl_per_lot?.toFixed(2) : '',
                              i === 0 ? s.pnl_total : '',
                              t.trade_no, t.strike, t.entry_time, t.ce_entry?.toFixed(2), t.pe_entry?.toFixed(2),
                              t.exit_time, t.ce_exit?.toFixed(2), t.pe_exit?.toFixed(2), t.exit_reason,
                              t.ce_pnl?.toFixed(2), t.pe_pnl?.toFixed(2), t.total_pnl?.toFixed(2)]);
                          });
                        } else { rows.push([day.date, s.nifty_open, s.nifty_close, s.nifty_range?.toFixed(0), s.total_trades, s.total_adjustments, s.pnl_per_lot?.toFixed(2), s.pnl_total]); }
                      });
                      const a = teData.aggregate;
                      rows.push([]);
                      rows.push(['SUMMARY','Trading Days:',a.trading_days,'Win Rate:',a.win_rate+'%','Total Trades:',a.total_trades,'Total P&L/Lot:',a.pnl_per_lot?.toFixed(2),'Total P&L:',a.pnl_total,'Avg P&L/Day:',a.avg_pnl_per_day?.toFixed(2)]);
                      const csv = rows.map(r => r.map(v => `"${v ?? ''}"`).join(',')).join('\n');
                      const blob = new Blob([csv], { type: 'text/csv' });
                      const url = URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url; link.download = `theta_expiry_${teData.from_date}_to_${teData.to_date}.csv`; link.click();
                      URL.revokeObjectURL(url);
                    }}>⬇ Export CSV</button>
                  )}
                </div>
              </div>
              {teLoading && (<div className="loading"><div className="spinner" /><p>Running Theta Expiry backtest... Each day takes ~30s.</p></div>)}
              {teError && <div className="error-banner">{teError}</div>}
              {teData && (
                <div className="bt-results">
                  <div className="bt-summary">
                    <div className="bt-summary-card"><span className="bt-label">Period</span><span className="bt-val">{teData.from_date} → {teData.to_date}</span></div>
                    <div className="bt-summary-card highlight"><span className="bt-label">Trading Days</span><span className="bt-val big">{teData.aggregate.trading_days}</span></div>
                    <div className="bt-summary-card highlight pnl-pos"><span className="bt-label">Winning Days</span><span className="bt-val big">{teData.aggregate.winning_days}</span></div>
                    <div className="bt-summary-card highlight pnl-neg"><span className="bt-label">Losing Days</span><span className="bt-val big">{teData.aggregate.losing_days}</span></div>
                    <div className="bt-summary-card highlight"><span className="bt-label">Win Rate</span><span className="bt-val big">{teData.aggregate.win_rate}%</span></div>
                    <div className="bt-summary-card highlight"><span className="bt-label">Total Trades</span><span className="bt-val big">{teData.aggregate.total_trades}</span></div>
                    <div className={`bt-summary-card highlight ${teData.aggregate.pnl_per_lot >= 0 ? 'pnl-pos' : 'pnl-neg'}`}><span className="bt-label">Total P&L / Lot</span><span className="bt-val big">{teData.aggregate.pnl_per_lot >= 0 ? '+' : ''}{teData.aggregate.pnl_per_lot?.toFixed(2)}</span></div>
                    <div className={`bt-summary-card highlight ${teData.aggregate.pnl_total >= 0 ? 'pnl-pos' : 'pnl-neg'}`}><span className="bt-label">Total P&L (₹)</span><span className="bt-val big">₹{teData.aggregate.pnl_total >= 0 ? '+' : ''}{teData.aggregate.pnl_total?.toLocaleString('en-IN')}</span></div>
                    <div className={`bt-summary-card ${teData.aggregate.avg_pnl_per_day >= 0 ? 'pnl-pos' : 'pnl-neg'}`}><span className="bt-label">Avg P&L / Day</span><span className="bt-val">{teData.aggregate.avg_pnl_per_day >= 0 ? '+' : ''}{teData.aggregate.avg_pnl_per_day?.toFixed(2)}</span></div>
                  </div>
                  <div className="bt-day-table">
                    <h3>Daily Breakdown</h3>
                    <div className="table-wrapper">
                      <table className="bt-trade-table">
                        <thead><tr><th>Date</th><th>Open</th><th>Close</th><th>Range</th><th>Trades</th><th>Adj</th><th>P&L/Lot</th><th>P&L (₹)</th><th></th></tr></thead>
                        <tbody>
                          {teData.days.map((day) => (
                            <React.Fragment key={day.date}>
                              <tr className={`day-row ${day.status !== 'ok' ? 'day-error' : day.summary?.pnl_per_lot >= 0 ? 'day-win' : 'day-loss'}`}
                                  onClick={() => setTeExpandedDay(teExpandedDay === day.date ? null : day.date)} style={{ cursor: 'pointer' }}>
                                <td><strong>{day.date}</strong></td>
                                {day.status === 'ok' ? (<>
                                    <td>{day.summary.nifty_open?.toLocaleString('en-IN')}</td>
                                    <td>{day.summary.nifty_close?.toLocaleString('en-IN')}</td>
                                    <td>{day.summary.nifty_range?.toFixed(0)}</td>
                                    <td>{day.summary.total_trades}</td>
                                    <td>{day.summary.total_adjustments}</td>
                                    <td className={day.summary.pnl_per_lot >= 0 ? 'positive' : 'negative'}><strong>{day.summary.pnl_per_lot >= 0 ? '+' : ''}{day.summary.pnl_per_lot?.toFixed(2)}</strong></td>
                                    <td className={day.summary.pnl_total >= 0 ? 'positive' : 'negative'}>₹{day.summary.pnl_total >= 0 ? '+' : ''}{day.summary.pnl_total?.toLocaleString('en-IN')}</td>
                                    <td className="expand-icon">{teExpandedDay === day.date ? '▼' : '▶'}</td>
                                </>) : (<><td colSpan="6" className="day-error-msg">{day.message || 'No data'}</td><td></td></>)}
                              </tr>
                              {teExpandedDay === day.date && day.status === 'ok' && (
                                <tr className="expanded-row"><td colSpan="9"><div className="bt-expanded-detail">
                                  <table className="bt-trade-table inner-table">
                                    <thead><tr><th>#</th><th>Strike</th><th>Entry</th><th>CE Sell ₹</th><th>PE Sell ₹</th><th>Exit</th><th>CE Buy ₹</th><th>PE Buy ₹</th><th>Reason</th><th>CE P&L</th><th>PE P&L</th><th>Total</th></tr></thead>
                                    <tbody>{day.trades.map((t, i) => (
                                      <tr key={i}><td>{t.trade_no}</td><td className="strike-cell">{t.strike}</td><td className="time-cell">{t.entry_time}</td><td>{t.ce_entry?.toFixed(2)}</td><td>{t.pe_entry?.toFixed(2)}</td><td className="time-cell">{t.exit_time}</td><td>{t.ce_exit?.toFixed(2)}</td><td>{t.pe_exit?.toFixed(2)}</td><td className="reason-cell">{t.exit_reason}</td><td className={t.ce_pnl >= 0 ? 'positive' : 'negative'}>{t.ce_pnl >= 0 ? '+' : ''}{t.ce_pnl?.toFixed(2)}</td><td className={t.pe_pnl >= 0 ? 'positive' : 'negative'}>{t.pe_pnl >= 0 ? '+' : ''}{t.pe_pnl?.toFixed(2)}</td><td className={`total-cell ${t.total_pnl >= 0 ? 'positive' : 'negative'}`}>{t.total_pnl >= 0 ? '+' : ''}{t.total_pnl?.toFixed(2)}</td></tr>
                                    ))}</tbody>
                                  </table>
                                </div></td></tr>
                              )}
                            </React.Fragment>
                          ))}
                          {teData.days.filter(d => d.status === 'ok').length > 1 && (
                            <tr className="total-row"><td><strong>TOTAL</strong></td><td colSpan="4"></td><td></td>
                              <td className={`total-cell ${teData.aggregate.pnl_per_lot >= 0 ? 'positive' : 'negative'}`}><strong>{teData.aggregate.pnl_per_lot >= 0 ? '+' : ''}{teData.aggregate.pnl_per_lot?.toFixed(2)}</strong></td>
                              <td className={`total-cell ${teData.aggregate.pnl_total >= 0 ? 'positive' : 'negative'}`}><strong>₹{teData.aggregate.pnl_total >= 0 ? '+' : ''}{teData.aggregate.pnl_total?.toLocaleString('en-IN')}</strong></td>
                              <td></td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ═══════ STRADDLE50 EXPIRY BACKTEST SECTION ═══════ */}
            <div className="bt-section">
              <div className="bt-header">
                <h2>📊 Straddle50 Expiry Backtest (Tuesday, 50pt)</h2>
                <div className="bt-controls">
                  <div className="bt-date-group">
                    <label>From</label>
                    <input type="date" className="bt-date-input" value={s50From}
                      onChange={(e) => setS50From(e.target.value)}
                      max={new Date().toISOString().split('T')[0]} />
                  </div>
                  <div className="bt-date-group">
                    <label>To</label>
                    <input type="date" className="bt-date-input" value={s50To}
                      onChange={(e) => setS50To(e.target.value)}
                      max={new Date().toISOString().split('T')[0]} />
                  </div>
                  <button className="bt-run-btn" onClick={loadBacktestS50} disabled={s50Loading}>
                    {s50Loading ? 'Running...' : 'Run Backtest'}
                  </button>
                  {s50Data && (
                    <button className="bt-export-btn" onClick={() => {
                      const rows = [['Date','Open','Close','Range','Trades','Adjustments','P&L/Lot','P&L Total','Trade#','Strike','Entry Time','CE Sell','PE Sell','Exit Time','CE Buy','PE Buy','Exit Reason','CE P&L','PE P&L','Trade P&L']];
                      s50Data.days.forEach(day => {
                        if (day.status !== 'ok') { rows.push([day.date,'','','','','','','',day.message || 'No data']); return; }
                        const s = day.summary;
                        if (day.trades?.length > 0) {
                          day.trades.forEach((t, i) => {
                            rows.push([i === 0 ? day.date : '', i === 0 ? s.nifty_open : '', i === 0 ? s.nifty_close : '',
                              i === 0 ? s.nifty_range?.toFixed(0) : '', i === 0 ? s.total_trades : '',
                              i === 0 ? s.total_adjustments : '', i === 0 ? s.pnl_per_lot?.toFixed(2) : '',
                              i === 0 ? s.pnl_total : '',
                              t.trade_no, t.strike, t.entry_time, t.ce_entry?.toFixed(2), t.pe_entry?.toFixed(2),
                              t.exit_time, t.ce_exit?.toFixed(2), t.pe_exit?.toFixed(2), t.exit_reason,
                              t.ce_pnl?.toFixed(2), t.pe_pnl?.toFixed(2), t.total_pnl?.toFixed(2)]);
                          });
                        } else { rows.push([day.date, s.nifty_open, s.nifty_close, s.nifty_range?.toFixed(0), s.total_trades, s.total_adjustments, s.pnl_per_lot?.toFixed(2), s.pnl_total]); }
                      });
                      const a = s50Data.aggregate;
                      rows.push([]);
                      rows.push(['SUMMARY','Trading Days:',a.trading_days,'Win Rate:',a.win_rate+'%','Total Trades:',a.total_trades,'Total P&L/Lot:',a.pnl_per_lot?.toFixed(2),'Total P&L:',a.pnl_total,'Avg P&L/Day:',a.avg_pnl_per_day?.toFixed(2)]);
                      const csv = rows.map(r => r.map(v => `"${v ?? ''}"`).join(',')).join('\n');
                      const blob = new Blob([csv], { type: 'text/csv' });
                      const url = URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url; link.download = `straddle50_expiry_${s50Data.from_date}_to_${s50Data.to_date}.csv`; link.click();
                      URL.revokeObjectURL(url);
                    }}>⬇ Export CSV</button>
                  )}
                </div>
              </div>
              {s50Loading && (<div className="loading"><div className="spinner" /><p>Running Straddle50 Expiry backtest... Each day takes ~30s.</p></div>)}
              {s50Error && <div className="error-banner">{s50Error}</div>}
              {s50Data && (
                <div className="bt-results">
                  <div className="bt-summary">
                    <div className="bt-summary-card"><span className="bt-label">Period</span><span className="bt-val">{s50Data.from_date} → {s50Data.to_date}</span></div>
                    <div className="bt-summary-card highlight"><span className="bt-label">Trading Days</span><span className="bt-val big">{s50Data.aggregate.trading_days}</span></div>
                    <div className="bt-summary-card highlight pnl-pos"><span className="bt-label">Winning Days</span><span className="bt-val big">{s50Data.aggregate.winning_days}</span></div>
                    <div className="bt-summary-card highlight pnl-neg"><span className="bt-label">Losing Days</span><span className="bt-val big">{s50Data.aggregate.losing_days}</span></div>
                    <div className="bt-summary-card highlight"><span className="bt-label">Win Rate</span><span className="bt-val big">{s50Data.aggregate.win_rate}%</span></div>
                    <div className="bt-summary-card highlight"><span className="bt-label">Total Trades</span><span className="bt-val big">{s50Data.aggregate.total_trades}</span></div>
                    <div className={`bt-summary-card highlight ${s50Data.aggregate.pnl_per_lot >= 0 ? 'pnl-pos' : 'pnl-neg'}`}><span className="bt-label">Total P&L / Lot</span><span className="bt-val big">{s50Data.aggregate.pnl_per_lot >= 0 ? '+' : ''}{s50Data.aggregate.pnl_per_lot?.toFixed(2)}</span></div>
                    <div className={`bt-summary-card highlight ${s50Data.aggregate.pnl_total >= 0 ? 'pnl-pos' : 'pnl-neg'}`}><span className="bt-label">Total P&L (₹)</span><span className="bt-val big">₹{s50Data.aggregate.pnl_total >= 0 ? '+' : ''}{s50Data.aggregate.pnl_total?.toLocaleString('en-IN')}</span></div>
                    <div className={`bt-summary-card ${s50Data.aggregate.avg_pnl_per_day >= 0 ? 'pnl-pos' : 'pnl-neg'}`}><span className="bt-label">Avg P&L / Day</span><span className="bt-val">{s50Data.aggregate.avg_pnl_per_day >= 0 ? '+' : ''}{s50Data.aggregate.avg_pnl_per_day?.toFixed(2)}</span></div>
                  </div>
                  <div className="bt-day-table">
                    <h3>Daily Breakdown</h3>
                    <div className="table-wrapper">
                      <table className="bt-trade-table">
                        <thead><tr><th>Date</th><th>Open</th><th>Close</th><th>Range</th><th>Trades</th><th>Adj</th><th>P&L/Lot</th><th>P&L (₹)</th><th></th></tr></thead>
                        <tbody>
                          {s50Data.days.map((day) => (
                            <React.Fragment key={day.date}>
                              <tr className={`day-row ${day.status !== 'ok' ? 'day-error' : day.summary?.pnl_per_lot >= 0 ? 'day-win' : 'day-loss'}`}
                                  onClick={() => setS50ExpandedDay(s50ExpandedDay === day.date ? null : day.date)} style={{ cursor: 'pointer' }}>
                                <td><strong>{day.date}</strong></td>
                                {day.status === 'ok' ? (<>
                                    <td>{day.summary.nifty_open?.toLocaleString('en-IN')}</td>
                                    <td>{day.summary.nifty_close?.toLocaleString('en-IN')}</td>
                                    <td>{day.summary.nifty_range?.toFixed(0)}</td>
                                    <td>{day.summary.total_trades}</td>
                                    <td>{day.summary.total_adjustments}</td>
                                    <td className={day.summary.pnl_per_lot >= 0 ? 'positive' : 'negative'}><strong>{day.summary.pnl_per_lot >= 0 ? '+' : ''}{day.summary.pnl_per_lot?.toFixed(2)}</strong></td>
                                    <td className={day.summary.pnl_total >= 0 ? 'positive' : 'negative'}>₹{day.summary.pnl_total >= 0 ? '+' : ''}{day.summary.pnl_total?.toLocaleString('en-IN')}</td>
                                    <td className="expand-icon">{s50ExpandedDay === day.date ? '▼' : '▶'}</td>
                                </>) : (<><td colSpan="6" className="day-error-msg">{day.message || 'No data'}</td><td></td></>)}
                              </tr>
                              {s50ExpandedDay === day.date && day.status === 'ok' && (
                                <tr className="expanded-row"><td colSpan="9"><div className="bt-expanded-detail">
                                  <table className="bt-trade-table inner-table">
                                    <thead><tr><th>#</th><th>Strike</th><th>Entry</th><th>CE Sell ₹</th><th>PE Sell ₹</th><th>Exit</th><th>CE Buy ₹</th><th>PE Buy ₹</th><th>Reason</th><th>CE P&L</th><th>PE P&L</th><th>Total</th></tr></thead>
                                    <tbody>{day.trades.map((t, i) => (
                                      <tr key={i}><td>{t.trade_no}</td><td className="strike-cell">{t.strike}</td><td className="time-cell">{t.entry_time}</td><td>{t.ce_entry?.toFixed(2)}</td><td>{t.pe_entry?.toFixed(2)}</td><td className="time-cell">{t.exit_time}</td><td>{t.ce_exit?.toFixed(2)}</td><td>{t.pe_exit?.toFixed(2)}</td><td className="reason-cell">{t.exit_reason}</td><td className={t.ce_pnl >= 0 ? 'positive' : 'negative'}>{t.ce_pnl >= 0 ? '+' : ''}{t.ce_pnl?.toFixed(2)}</td><td className={t.pe_pnl >= 0 ? 'positive' : 'negative'}>{t.pe_pnl >= 0 ? '+' : ''}{t.pe_pnl?.toFixed(2)}</td><td className={`total-cell ${t.total_pnl >= 0 ? 'positive' : 'negative'}`}>{t.total_pnl >= 0 ? '+' : ''}{t.total_pnl?.toFixed(2)}</td></tr>
                                    ))}</tbody>
                                  </table>
                                </div></td></tr>
                              )}
                            </React.Fragment>
                          ))}
                          {s50Data.days.filter(d => d.status === 'ok').length > 1 && (
                            <tr className="total-row"><td><strong>TOTAL</strong></td><td colSpan="4"></td><td></td>
                              <td className={`total-cell ${s50Data.aggregate.pnl_per_lot >= 0 ? 'positive' : 'negative'}`}><strong>{s50Data.aggregate.pnl_per_lot >= 0 ? '+' : ''}{s50Data.aggregate.pnl_per_lot?.toFixed(2)}</strong></td>
                              <td className={`total-cell ${s50Data.aggregate.pnl_total >= 0 ? 'positive' : 'negative'}`}><strong>₹{s50Data.aggregate.pnl_total >= 0 ? '+' : ''}{s50Data.aggregate.pnl_total?.toLocaleString('en-IN')}</strong></td>
                              <td></td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ═══════ THETA SENSEX WED BACKTEST SECTION ═══════ */}
            <div className="bt-section">
              <div className="bt-header">
                <h2>📊 Theta SENSEX Wednesday Backtest (Wed, 300pt)</h2>
                <div className="bt-controls">
                  <div className="bt-date-group"><label>From</label><input type="date" className="bt-date-input" value={tswFrom} onChange={(e) => setTswFrom(e.target.value)} max={new Date().toISOString().split('T')[0]} /></div>
                  <div className="bt-date-group"><label>To</label><input type="date" className="bt-date-input" value={tswTo} onChange={(e) => setTswTo(e.target.value)} max={new Date().toISOString().split('T')[0]} /></div>
                  <button className="bt-run-btn" onClick={loadBacktestTSW} disabled={tswLoading}>{tswLoading ? 'Running...' : 'Run Backtest'}</button>
                  {tswData && (
                    <button className="bt-export-btn" onClick={() => {
                      const rows = [['Date','Open','Close','Range','Trades','Adjustments','P&L/Lot','P&L Total','Trade#','Strike','Entry Time','CE Sell','PE Sell','Exit Time','CE Buy','PE Buy','Exit Reason','CE P&L','PE P&L','Trade P&L']];
                      tswData.days.forEach(day => { if (day.status !== 'ok') { rows.push([day.date,'','','','','','','',day.message || 'No data']); return; } const s = day.summary; if (day.trades?.length > 0) { day.trades.forEach((t, i) => { rows.push([i === 0 ? day.date : '', i === 0 ? s.nifty_open : '', i === 0 ? s.nifty_close : '', i === 0 ? s.nifty_range?.toFixed(0) : '', i === 0 ? s.total_trades : '', i === 0 ? s.total_adjustments : '', i === 0 ? s.pnl_per_lot?.toFixed(2) : '', i === 0 ? s.pnl_total : '', t.trade_no, t.strike, t.entry_time, t.ce_entry?.toFixed(2), t.pe_entry?.toFixed(2), t.exit_time, t.ce_exit?.toFixed(2), t.pe_exit?.toFixed(2), t.exit_reason, t.ce_pnl?.toFixed(2), t.pe_pnl?.toFixed(2), t.total_pnl?.toFixed(2)]); }); } else { rows.push([day.date, s.nifty_open, s.nifty_close, s.nifty_range?.toFixed(0), s.total_trades, s.total_adjustments, s.pnl_per_lot?.toFixed(2), s.pnl_total]); } });
                      const a = tswData.aggregate; rows.push([]); rows.push(['SUMMARY','Trading Days:',a.trading_days,'Win Rate:',a.win_rate+'%','Total Trades:',a.total_trades,'Total P&L/Lot:',a.pnl_per_lot?.toFixed(2),'Total P&L:',a.pnl_total,'Avg P&L/Day:',a.avg_pnl_per_day?.toFixed(2)]);
                      const csv = rows.map(r => r.map(v => `"${v ?? ''}"`).join(',')).join('\n'); const blob = new Blob([csv], { type: 'text/csv' }); const url = URL.createObjectURL(blob); const link = document.createElement('a'); link.href = url; link.download = `theta_sensex_wed_${tswData.from_date}_to_${tswData.to_date}.csv`; link.click(); URL.revokeObjectURL(url);
                    }}>⬇ Export CSV</button>
                  )}
                </div>
              </div>
              {tswLoading && (<div className="loading"><div className="spinner" /><p>Running Theta SENSEX Wed backtest... Each day takes ~30s.</p></div>)}
              {tswError && <div className="error-banner">{tswError}</div>}
              {tswData && (
                <div className="bt-results">
                  <div className="bt-summary">
                    <div className="bt-summary-card"><span className="bt-label">Period</span><span className="bt-val">{tswData.from_date} → {tswData.to_date}</span></div>
                    <div className="bt-summary-card highlight"><span className="bt-label">Trading Days</span><span className="bt-val big">{tswData.aggregate.trading_days}</span></div>
                    <div className="bt-summary-card highlight pnl-pos"><span className="bt-label">Winning Days</span><span className="bt-val big">{tswData.aggregate.winning_days}</span></div>
                    <div className="bt-summary-card highlight pnl-neg"><span className="bt-label">Losing Days</span><span className="bt-val big">{tswData.aggregate.losing_days}</span></div>
                    <div className="bt-summary-card highlight"><span className="bt-label">Win Rate</span><span className="bt-val big">{tswData.aggregate.win_rate}%</span></div>
                    <div className="bt-summary-card highlight"><span className="bt-label">Total Trades</span><span className="bt-val big">{tswData.aggregate.total_trades}</span></div>
                    <div className={`bt-summary-card highlight ${tswData.aggregate.pnl_per_lot >= 0 ? 'pnl-pos' : 'pnl-neg'}`}><span className="bt-label">Total P&L / Lot</span><span className="bt-val big">{tswData.aggregate.pnl_per_lot >= 0 ? '+' : ''}{tswData.aggregate.pnl_per_lot?.toFixed(2)}</span></div>
                    <div className={`bt-summary-card highlight ${tswData.aggregate.pnl_total >= 0 ? 'pnl-pos' : 'pnl-neg'}`}><span className="bt-label">Total P&L (₹)</span><span className="bt-val big">₹{tswData.aggregate.pnl_total >= 0 ? '+' : ''}{tswData.aggregate.pnl_total?.toLocaleString('en-IN')}</span></div>
                    <div className={`bt-summary-card ${tswData.aggregate.avg_pnl_per_day >= 0 ? 'pnl-pos' : 'pnl-neg'}`}><span className="bt-label">Avg P&L / Day</span><span className="bt-val">{tswData.aggregate.avg_pnl_per_day >= 0 ? '+' : ''}{tswData.aggregate.avg_pnl_per_day?.toFixed(2)}</span></div>
                  </div>
                  <div className="bt-day-table"><h3>Daily Breakdown</h3><div className="table-wrapper"><table className="bt-trade-table">
                    <thead><tr><th>Date</th><th>Open</th><th>Close</th><th>Range</th><th>Trades</th><th>Adj</th><th>P&L/Lot</th><th>P&L (₹)</th><th></th></tr></thead>
                    <tbody>
                      {tswData.days.map((day) => (<React.Fragment key={day.date}>
                        <tr className={`day-row ${day.status !== 'ok' ? 'day-error' : day.summary?.pnl_per_lot >= 0 ? 'day-win' : 'day-loss'}`} onClick={() => setTswExpandedDay(tswExpandedDay === day.date ? null : day.date)} style={{ cursor: 'pointer' }}>
                          <td><strong>{day.date}</strong></td>
                          {day.status === 'ok' ? (<><td>{day.summary.nifty_open?.toLocaleString('en-IN')}</td><td>{day.summary.nifty_close?.toLocaleString('en-IN')}</td><td>{day.summary.nifty_range?.toFixed(0)}</td><td>{day.summary.total_trades}</td><td>{day.summary.total_adjustments}</td><td className={day.summary.pnl_per_lot >= 0 ? 'positive' : 'negative'}><strong>{day.summary.pnl_per_lot >= 0 ? '+' : ''}{day.summary.pnl_per_lot?.toFixed(2)}</strong></td><td className={day.summary.pnl_total >= 0 ? 'positive' : 'negative'}>₹{day.summary.pnl_total >= 0 ? '+' : ''}{day.summary.pnl_total?.toLocaleString('en-IN')}</td><td className="expand-icon">{tswExpandedDay === day.date ? '▼' : '▶'}</td></>) : (<><td colSpan="6" className="day-error-msg">{day.message || 'No data'}</td><td></td></>)}
                        </tr>
                        {tswExpandedDay === day.date && day.status === 'ok' && (<tr className="expanded-row"><td colSpan="9"><div className="bt-expanded-detail"><table className="bt-trade-table inner-table"><thead><tr><th>#</th><th>Strike</th><th>Entry</th><th>CE Sell ₹</th><th>PE Sell ₹</th><th>Exit</th><th>CE Buy ₹</th><th>PE Buy ₹</th><th>Reason</th><th>CE P&L</th><th>PE P&L</th><th>Total</th></tr></thead><tbody>{day.trades.map((t, i) => (<tr key={i}><td>{t.trade_no}</td><td className="strike-cell">{t.strike}</td><td className="time-cell">{t.entry_time}</td><td>{t.ce_entry?.toFixed(2)}</td><td>{t.pe_entry?.toFixed(2)}</td><td className="time-cell">{t.exit_time}</td><td>{t.ce_exit?.toFixed(2)}</td><td>{t.pe_exit?.toFixed(2)}</td><td className="reason-cell">{t.exit_reason}</td><td className={t.ce_pnl >= 0 ? 'positive' : 'negative'}>{t.ce_pnl >= 0 ? '+' : ''}{t.ce_pnl?.toFixed(2)}</td><td className={t.pe_pnl >= 0 ? 'positive' : 'negative'}>{t.pe_pnl >= 0 ? '+' : ''}{t.pe_pnl?.toFixed(2)}</td><td className={`total-cell ${t.total_pnl >= 0 ? 'positive' : 'negative'}`}>{t.total_pnl >= 0 ? '+' : ''}{t.total_pnl?.toFixed(2)}</td></tr>))}</tbody></table></div></td></tr>)}
                      </React.Fragment>))}
                      {tswData.days.filter(d => d.status === 'ok').length > 1 && (<tr className="total-row"><td><strong>TOTAL</strong></td><td colSpan="4"></td><td></td><td className={`total-cell ${tswData.aggregate.pnl_per_lot >= 0 ? 'positive' : 'negative'}`}><strong>{tswData.aggregate.pnl_per_lot >= 0 ? '+' : ''}{tswData.aggregate.pnl_per_lot?.toFixed(2)}</strong></td><td className={`total-cell ${tswData.aggregate.pnl_total >= 0 ? 'positive' : 'negative'}`}><strong>₹{tswData.aggregate.pnl_total >= 0 ? '+' : ''}{tswData.aggregate.pnl_total?.toLocaleString('en-IN')}</strong></td><td></td></tr>)}
                    </tbody>
                  </table></div></div>
                </div>
              )}
            </div>

            {/* ═══════ STRADDLE300 SENSEX WED BACKTEST SECTION ═══════ */}
            <div className="bt-section">
              <div className="bt-header">
                <h2>📊 Straddle300 SENSEX Wednesday Backtest (Wed, 300pt)</h2>
                <div className="bt-controls">
                  <div className="bt-date-group"><label>From</label><input type="date" className="bt-date-input" value={s3swFrom} onChange={(e) => setS3swFrom(e.target.value)} max={new Date().toISOString().split('T')[0]} /></div>
                  <div className="bt-date-group"><label>To</label><input type="date" className="bt-date-input" value={s3swTo} onChange={(e) => setS3swTo(e.target.value)} max={new Date().toISOString().split('T')[0]} /></div>
                  <button className="bt-run-btn" onClick={loadBacktestS3SW} disabled={s3swLoading}>{s3swLoading ? 'Running...' : 'Run Backtest'}</button>
                  {s3swData && (
                    <button className="bt-export-btn" onClick={() => {
                      const rows = [['Date','Open','Close','Range','Trades','Adjustments','P&L/Lot','P&L Total','Trade#','Strike','Entry Time','CE Sell','PE Sell','Exit Time','CE Buy','PE Buy','Exit Reason','CE P&L','PE P&L','Trade P&L']];
                      s3swData.days.forEach(day => { if (day.status !== 'ok') { rows.push([day.date,'','','','','','','',day.message || 'No data']); return; } const s = day.summary; if (day.trades?.length > 0) { day.trades.forEach((t, i) => { rows.push([i === 0 ? day.date : '', i === 0 ? s.nifty_open : '', i === 0 ? s.nifty_close : '', i === 0 ? s.nifty_range?.toFixed(0) : '', i === 0 ? s.total_trades : '', i === 0 ? s.total_adjustments : '', i === 0 ? s.pnl_per_lot?.toFixed(2) : '', i === 0 ? s.pnl_total : '', t.trade_no, t.strike, t.entry_time, t.ce_entry?.toFixed(2), t.pe_entry?.toFixed(2), t.exit_time, t.ce_exit?.toFixed(2), t.pe_exit?.toFixed(2), t.exit_reason, t.ce_pnl?.toFixed(2), t.pe_pnl?.toFixed(2), t.total_pnl?.toFixed(2)]); }); } else { rows.push([day.date, s.nifty_open, s.nifty_close, s.nifty_range?.toFixed(0), s.total_trades, s.total_adjustments, s.pnl_per_lot?.toFixed(2), s.pnl_total]); } });
                      const a = s3swData.aggregate; rows.push([]); rows.push(['SUMMARY','Trading Days:',a.trading_days,'Win Rate:',a.win_rate+'%','Total Trades:',a.total_trades,'Total P&L/Lot:',a.pnl_per_lot?.toFixed(2),'Total P&L:',a.pnl_total,'Avg P&L/Day:',a.avg_pnl_per_day?.toFixed(2)]);
                      const csv = rows.map(r => r.map(v => `"${v ?? ''}"`).join(',')).join('\n'); const blob = new Blob([csv], { type: 'text/csv' }); const url = URL.createObjectURL(blob); const link = document.createElement('a'); link.href = url; link.download = `straddle300_sensex_wed_${s3swData.from_date}_to_${s3swData.to_date}.csv`; link.click(); URL.revokeObjectURL(url);
                    }}>⬇ Export CSV</button>
                  )}
                </div>
              </div>
              {s3swLoading && (<div className="loading"><div className="spinner" /><p>Running Straddle300 SENSEX Wed backtest... Each day takes ~30s.</p></div>)}
              {s3swError && <div className="error-banner">{s3swError}</div>}
              {s3swData && (
                <div className="bt-results">
                  <div className="bt-summary">
                    <div className="bt-summary-card"><span className="bt-label">Period</span><span className="bt-val">{s3swData.from_date} → {s3swData.to_date}</span></div>
                    <div className="bt-summary-card highlight"><span className="bt-label">Trading Days</span><span className="bt-val big">{s3swData.aggregate.trading_days}</span></div>
                    <div className="bt-summary-card highlight pnl-pos"><span className="bt-label">Winning Days</span><span className="bt-val big">{s3swData.aggregate.winning_days}</span></div>
                    <div className="bt-summary-card highlight pnl-neg"><span className="bt-label">Losing Days</span><span className="bt-val big">{s3swData.aggregate.losing_days}</span></div>
                    <div className="bt-summary-card highlight"><span className="bt-label">Win Rate</span><span className="bt-val big">{s3swData.aggregate.win_rate}%</span></div>
                    <div className="bt-summary-card highlight"><span className="bt-label">Total Trades</span><span className="bt-val big">{s3swData.aggregate.total_trades}</span></div>
                    <div className={`bt-summary-card highlight ${s3swData.aggregate.pnl_per_lot >= 0 ? 'pnl-pos' : 'pnl-neg'}`}><span className="bt-label">Total P&L / Lot</span><span className="bt-val big">{s3swData.aggregate.pnl_per_lot >= 0 ? '+' : ''}{s3swData.aggregate.pnl_per_lot?.toFixed(2)}</span></div>
                    <div className={`bt-summary-card highlight ${s3swData.aggregate.pnl_total >= 0 ? 'pnl-pos' : 'pnl-neg'}`}><span className="bt-label">Total P&L (₹)</span><span className="bt-val big">₹{s3swData.aggregate.pnl_total >= 0 ? '+' : ''}{s3swData.aggregate.pnl_total?.toLocaleString('en-IN')}</span></div>
                    <div className={`bt-summary-card ${s3swData.aggregate.avg_pnl_per_day >= 0 ? 'pnl-pos' : 'pnl-neg'}`}><span className="bt-label">Avg P&L / Day</span><span className="bt-val">{s3swData.aggregate.avg_pnl_per_day >= 0 ? '+' : ''}{s3swData.aggregate.avg_pnl_per_day?.toFixed(2)}</span></div>
                  </div>
                  <div className="bt-day-table"><h3>Daily Breakdown</h3><div className="table-wrapper"><table className="bt-trade-table">
                    <thead><tr><th>Date</th><th>Open</th><th>Close</th><th>Range</th><th>Trades</th><th>Adj</th><th>P&L/Lot</th><th>P&L (₹)</th><th></th></tr></thead>
                    <tbody>
                      {s3swData.days.map((day) => (<React.Fragment key={day.date}>
                        <tr className={`day-row ${day.status !== 'ok' ? 'day-error' : day.summary?.pnl_per_lot >= 0 ? 'day-win' : 'day-loss'}`} onClick={() => setS3swExpandedDay(s3swExpandedDay === day.date ? null : day.date)} style={{ cursor: 'pointer' }}>
                          <td><strong>{day.date}</strong></td>
                          {day.status === 'ok' ? (<><td>{day.summary.nifty_open?.toLocaleString('en-IN')}</td><td>{day.summary.nifty_close?.toLocaleString('en-IN')}</td><td>{day.summary.nifty_range?.toFixed(0)}</td><td>{day.summary.total_trades}</td><td>{day.summary.total_adjustments}</td><td className={day.summary.pnl_per_lot >= 0 ? 'positive' : 'negative'}><strong>{day.summary.pnl_per_lot >= 0 ? '+' : ''}{day.summary.pnl_per_lot?.toFixed(2)}</strong></td><td className={day.summary.pnl_total >= 0 ? 'positive' : 'negative'}>₹{day.summary.pnl_total >= 0 ? '+' : ''}{day.summary.pnl_total?.toLocaleString('en-IN')}</td><td className="expand-icon">{s3swExpandedDay === day.date ? '▼' : '▶'}</td></>) : (<><td colSpan="6" className="day-error-msg">{day.message || 'No data'}</td><td></td></>)}
                        </tr>
                        {s3swExpandedDay === day.date && day.status === 'ok' && (<tr className="expanded-row"><td colSpan="9"><div className="bt-expanded-detail"><table className="bt-trade-table inner-table"><thead><tr><th>#</th><th>Strike</th><th>Entry</th><th>CE Sell ₹</th><th>PE Sell ₹</th><th>Exit</th><th>CE Buy ₹</th><th>PE Buy ₹</th><th>Reason</th><th>CE P&L</th><th>PE P&L</th><th>Total</th></tr></thead><tbody>{day.trades.map((t, i) => (<tr key={i}><td>{t.trade_no}</td><td className="strike-cell">{t.strike}</td><td className="time-cell">{t.entry_time}</td><td>{t.ce_entry?.toFixed(2)}</td><td>{t.pe_entry?.toFixed(2)}</td><td className="time-cell">{t.exit_time}</td><td>{t.ce_exit?.toFixed(2)}</td><td>{t.pe_exit?.toFixed(2)}</td><td className="reason-cell">{t.exit_reason}</td><td className={t.ce_pnl >= 0 ? 'positive' : 'negative'}>{t.ce_pnl >= 0 ? '+' : ''}{t.ce_pnl?.toFixed(2)}</td><td className={t.pe_pnl >= 0 ? 'positive' : 'negative'}>{t.pe_pnl >= 0 ? '+' : ''}{t.pe_pnl?.toFixed(2)}</td><td className={`total-cell ${t.total_pnl >= 0 ? 'positive' : 'negative'}`}>{t.total_pnl >= 0 ? '+' : ''}{t.total_pnl?.toFixed(2)}</td></tr>))}</tbody></table></div></td></tr>)}
                      </React.Fragment>))}
                      {s3swData.days.filter(d => d.status === 'ok').length > 1 && (<tr className="total-row"><td><strong>TOTAL</strong></td><td colSpan="4"></td><td></td><td className={`total-cell ${s3swData.aggregate.pnl_per_lot >= 0 ? 'positive' : 'negative'}`}><strong>{s3swData.aggregate.pnl_per_lot >= 0 ? '+' : ''}{s3swData.aggregate.pnl_per_lot?.toFixed(2)}</strong></td><td className={`total-cell ${s3swData.aggregate.pnl_total >= 0 ? 'positive' : 'negative'}`}><strong>₹{s3swData.aggregate.pnl_total >= 0 ? '+' : ''}{s3swData.aggregate.pnl_total?.toLocaleString('en-IN')}</strong></td><td></td></tr>)}
                    </tbody>
                  </table></div></div>
                </div>
              )}
            </div>

            {/* ═══════ THETA SENSEX EXPIRY BACKTEST SECTION ═══════ */}
            <div className="bt-section">
              <div className="bt-header">
                <h2>📊 Theta SENSEX Expiry Backtest (Thu, 100pt)</h2>
                <div className="bt-controls">
                  <div className="bt-date-group"><label>From</label><input type="date" className="bt-date-input" value={tseFrom} onChange={(e) => setTseFrom(e.target.value)} max={new Date().toISOString().split('T')[0]} /></div>
                  <div className="bt-date-group"><label>To</label><input type="date" className="bt-date-input" value={tseTo} onChange={(e) => setTseTo(e.target.value)} max={new Date().toISOString().split('T')[0]} /></div>
                  <button className="bt-run-btn" onClick={loadBacktestTSE} disabled={tseLoading}>{tseLoading ? 'Running...' : 'Run Backtest'}</button>
                  {tseData && (
                    <button className="bt-export-btn" onClick={() => {
                      const rows = [['Date','Open','Close','Range','Trades','Adjustments','P&L/Lot','P&L Total','Trade#','Strike','Entry Time','CE Sell','PE Sell','Exit Time','CE Buy','PE Buy','Exit Reason','CE P&L','PE P&L','Trade P&L']];
                      tseData.days.forEach(day => { if (day.status !== 'ok') { rows.push([day.date,'','','','','','','',day.message || 'No data']); return; } const s = day.summary; if (day.trades?.length > 0) { day.trades.forEach((t, i) => { rows.push([i === 0 ? day.date : '', i === 0 ? s.nifty_open : '', i === 0 ? s.nifty_close : '', i === 0 ? s.nifty_range?.toFixed(0) : '', i === 0 ? s.total_trades : '', i === 0 ? s.total_adjustments : '', i === 0 ? s.pnl_per_lot?.toFixed(2) : '', i === 0 ? s.pnl_total : '', t.trade_no, t.strike, t.entry_time, t.ce_entry?.toFixed(2), t.pe_entry?.toFixed(2), t.exit_time, t.ce_exit?.toFixed(2), t.pe_exit?.toFixed(2), t.exit_reason, t.ce_pnl?.toFixed(2), t.pe_pnl?.toFixed(2), t.total_pnl?.toFixed(2)]); }); } else { rows.push([day.date, s.nifty_open, s.nifty_close, s.nifty_range?.toFixed(0), s.total_trades, s.total_adjustments, s.pnl_per_lot?.toFixed(2), s.pnl_total]); } });
                      const a = tseData.aggregate; rows.push([]); rows.push(['SUMMARY','Trading Days:',a.trading_days,'Win Rate:',a.win_rate+'%','Total Trades:',a.total_trades,'Total P&L/Lot:',a.pnl_per_lot?.toFixed(2),'Total P&L:',a.pnl_total,'Avg P&L/Day:',a.avg_pnl_per_day?.toFixed(2)]);
                      const csv = rows.map(r => r.map(v => `"${v ?? ''}"`).join(',')).join('\n'); const blob = new Blob([csv], { type: 'text/csv' }); const url = URL.createObjectURL(blob); const link = document.createElement('a'); link.href = url; link.download = `theta_sensex_expiry_${tseData.from_date}_to_${tseData.to_date}.csv`; link.click(); URL.revokeObjectURL(url);
                    }}>⬇ Export CSV</button>
                  )}
                </div>
              </div>
              {tseLoading && (<div className="loading"><div className="spinner" /><p>Running Theta SENSEX Expiry backtest... Each day takes ~30s.</p></div>)}
              {tseError && <div className="error-banner">{tseError}</div>}
              {tseData && (
                <div className="bt-results">
                  <div className="bt-summary">
                    <div className="bt-summary-card"><span className="bt-label">Period</span><span className="bt-val">{tseData.from_date} → {tseData.to_date}</span></div>
                    <div className="bt-summary-card highlight"><span className="bt-label">Trading Days</span><span className="bt-val big">{tseData.aggregate.trading_days}</span></div>
                    <div className="bt-summary-card highlight pnl-pos"><span className="bt-label">Winning Days</span><span className="bt-val big">{tseData.aggregate.winning_days}</span></div>
                    <div className="bt-summary-card highlight pnl-neg"><span className="bt-label">Losing Days</span><span className="bt-val big">{tseData.aggregate.losing_days}</span></div>
                    <div className="bt-summary-card highlight"><span className="bt-label">Win Rate</span><span className="bt-val big">{tseData.aggregate.win_rate}%</span></div>
                    <div className="bt-summary-card highlight"><span className="bt-label">Total Trades</span><span className="bt-val big">{tseData.aggregate.total_trades}</span></div>
                    <div className={`bt-summary-card highlight ${tseData.aggregate.pnl_per_lot >= 0 ? 'pnl-pos' : 'pnl-neg'}`}><span className="bt-label">Total P&L / Lot</span><span className="bt-val big">{tseData.aggregate.pnl_per_lot >= 0 ? '+' : ''}{tseData.aggregate.pnl_per_lot?.toFixed(2)}</span></div>
                    <div className={`bt-summary-card highlight ${tseData.aggregate.pnl_total >= 0 ? 'pnl-pos' : 'pnl-neg'}`}><span className="bt-label">Total P&L (₹)</span><span className="bt-val big">₹{tseData.aggregate.pnl_total >= 0 ? '+' : ''}{tseData.aggregate.pnl_total?.toLocaleString('en-IN')}</span></div>
                    <div className={`bt-summary-card ${tseData.aggregate.avg_pnl_per_day >= 0 ? 'pnl-pos' : 'pnl-neg'}`}><span className="bt-label">Avg P&L / Day</span><span className="bt-val">{tseData.aggregate.avg_pnl_per_day >= 0 ? '+' : ''}{tseData.aggregate.avg_pnl_per_day?.toFixed(2)}</span></div>
                  </div>
                  <div className="bt-day-table"><h3>Daily Breakdown</h3><div className="table-wrapper"><table className="bt-trade-table">
                    <thead><tr><th>Date</th><th>Open</th><th>Close</th><th>Range</th><th>Trades</th><th>Adj</th><th>P&L/Lot</th><th>P&L (₹)</th><th></th></tr></thead>
                    <tbody>
                      {tseData.days.map((day) => (<React.Fragment key={day.date}>
                        <tr className={`day-row ${day.status !== 'ok' ? 'day-error' : day.summary?.pnl_per_lot >= 0 ? 'day-win' : 'day-loss'}`} onClick={() => setTseExpandedDay(tseExpandedDay === day.date ? null : day.date)} style={{ cursor: 'pointer' }}>
                          <td><strong>{day.date}</strong></td>
                          {day.status === 'ok' ? (<><td>{day.summary.nifty_open?.toLocaleString('en-IN')}</td><td>{day.summary.nifty_close?.toLocaleString('en-IN')}</td><td>{day.summary.nifty_range?.toFixed(0)}</td><td>{day.summary.total_trades}</td><td>{day.summary.total_adjustments}</td><td className={day.summary.pnl_per_lot >= 0 ? 'positive' : 'negative'}><strong>{day.summary.pnl_per_lot >= 0 ? '+' : ''}{day.summary.pnl_per_lot?.toFixed(2)}</strong></td><td className={day.summary.pnl_total >= 0 ? 'positive' : 'negative'}>₹{day.summary.pnl_total >= 0 ? '+' : ''}{day.summary.pnl_total?.toLocaleString('en-IN')}</td><td className="expand-icon">{tseExpandedDay === day.date ? '▼' : '▶'}</td></>) : (<><td colSpan="6" className="day-error-msg">{day.message || 'No data'}</td><td></td></>)}
                        </tr>
                        {tseExpandedDay === day.date && day.status === 'ok' && (<tr className="expanded-row"><td colSpan="9"><div className="bt-expanded-detail"><table className="bt-trade-table inner-table"><thead><tr><th>#</th><th>Strike</th><th>Entry</th><th>CE Sell ₹</th><th>PE Sell ₹</th><th>Exit</th><th>CE Buy ₹</th><th>PE Buy ₹</th><th>Reason</th><th>CE P&L</th><th>PE P&L</th><th>Total</th></tr></thead><tbody>{day.trades.map((t, i) => (<tr key={i}><td>{t.trade_no}</td><td className="strike-cell">{t.strike}</td><td className="time-cell">{t.entry_time}</td><td>{t.ce_entry?.toFixed(2)}</td><td>{t.pe_entry?.toFixed(2)}</td><td className="time-cell">{t.exit_time}</td><td>{t.ce_exit?.toFixed(2)}</td><td>{t.pe_exit?.toFixed(2)}</td><td className="reason-cell">{t.exit_reason}</td><td className={t.ce_pnl >= 0 ? 'positive' : 'negative'}>{t.ce_pnl >= 0 ? '+' : ''}{t.ce_pnl?.toFixed(2)}</td><td className={t.pe_pnl >= 0 ? 'positive' : 'negative'}>{t.pe_pnl >= 0 ? '+' : ''}{t.pe_pnl?.toFixed(2)}</td><td className={`total-cell ${t.total_pnl >= 0 ? 'positive' : 'negative'}`}>{t.total_pnl >= 0 ? '+' : ''}{t.total_pnl?.toFixed(2)}</td></tr>))}</tbody></table></div></td></tr>)}
                      </React.Fragment>))}
                      {tseData.days.filter(d => d.status === 'ok').length > 1 && (<tr className="total-row"><td><strong>TOTAL</strong></td><td colSpan="4"></td><td></td><td className={`total-cell ${tseData.aggregate.pnl_per_lot >= 0 ? 'positive' : 'negative'}`}><strong>{tseData.aggregate.pnl_per_lot >= 0 ? '+' : ''}{tseData.aggregate.pnl_per_lot?.toFixed(2)}</strong></td><td className={`total-cell ${tseData.aggregate.pnl_total >= 0 ? 'positive' : 'negative'}`}><strong>₹{tseData.aggregate.pnl_total >= 0 ? '+' : ''}{tseData.aggregate.pnl_total?.toLocaleString('en-IN')}</strong></td><td></td></tr>)}
                    </tbody>
                  </table></div></div>
                </div>
              )}
            </div>

            {/* ═══════ STRADDLE100 SENSEX EXPIRY BACKTEST SECTION ═══════ */}
            <div className="bt-section">
              <div className="bt-header">
                <h2>📊 Straddle100 SENSEX Expiry Backtest (Thu, 100pt)</h2>
                <div className="bt-controls">
                  <div className="bt-date-group"><label>From</label><input type="date" className="bt-date-input" value={s1seFrom} onChange={(e) => setS1seFrom(e.target.value)} max={new Date().toISOString().split('T')[0]} /></div>
                  <div className="bt-date-group"><label>To</label><input type="date" className="bt-date-input" value={s1seTo} onChange={(e) => setS1seTo(e.target.value)} max={new Date().toISOString().split('T')[0]} /></div>
                  <button className="bt-run-btn" onClick={loadBacktestS1SE} disabled={s1seLoading}>{s1seLoading ? 'Running...' : 'Run Backtest'}</button>
                  {s1seData && (
                    <button className="bt-export-btn" onClick={() => {
                      const rows = [['Date','Open','Close','Range','Trades','Adjustments','P&L/Lot','P&L Total','Trade#','Strike','Entry Time','CE Sell','PE Sell','Exit Time','CE Buy','PE Buy','Exit Reason','CE P&L','PE P&L','Trade P&L']];
                      s1seData.days.forEach(day => { if (day.status !== 'ok') { rows.push([day.date,'','','','','','','',day.message || 'No data']); return; } const s = day.summary; if (day.trades?.length > 0) { day.trades.forEach((t, i) => { rows.push([i === 0 ? day.date : '', i === 0 ? s.nifty_open : '', i === 0 ? s.nifty_close : '', i === 0 ? s.nifty_range?.toFixed(0) : '', i === 0 ? s.total_trades : '', i === 0 ? s.total_adjustments : '', i === 0 ? s.pnl_per_lot?.toFixed(2) : '', i === 0 ? s.pnl_total : '', t.trade_no, t.strike, t.entry_time, t.ce_entry?.toFixed(2), t.pe_entry?.toFixed(2), t.exit_time, t.ce_exit?.toFixed(2), t.pe_exit?.toFixed(2), t.exit_reason, t.ce_pnl?.toFixed(2), t.pe_pnl?.toFixed(2), t.total_pnl?.toFixed(2)]); }); } else { rows.push([day.date, s.nifty_open, s.nifty_close, s.nifty_range?.toFixed(0), s.total_trades, s.total_adjustments, s.pnl_per_lot?.toFixed(2), s.pnl_total]); } });
                      const a = s1seData.aggregate; rows.push([]); rows.push(['SUMMARY','Trading Days:',a.trading_days,'Win Rate:',a.win_rate+'%','Total Trades:',a.total_trades,'Total P&L/Lot:',a.pnl_per_lot?.toFixed(2),'Total P&L:',a.pnl_total,'Avg P&L/Day:',a.avg_pnl_per_day?.toFixed(2)]);
                      const csv = rows.map(r => r.map(v => `"${v ?? ''}"`).join(',')).join('\n'); const blob = new Blob([csv], { type: 'text/csv' }); const url = URL.createObjectURL(blob); const link = document.createElement('a'); link.href = url; link.download = `straddle100_sensex_expiry_${s1seData.from_date}_to_${s1seData.to_date}.csv`; link.click(); URL.revokeObjectURL(url);
                    }}>⬇ Export CSV</button>
                  )}
                </div>
              </div>
              {s1seLoading && (<div className="loading"><div className="spinner" /><p>Running Straddle100 SENSEX Expiry backtest... Each day takes ~30s.</p></div>)}
              {s1seError && <div className="error-banner">{s1seError}</div>}
              {s1seData && (
                <div className="bt-results">
                  <div className="bt-summary">
                    <div className="bt-summary-card"><span className="bt-label">Period</span><span className="bt-val">{s1seData.from_date} → {s1seData.to_date}</span></div>
                    <div className="bt-summary-card highlight"><span className="bt-label">Trading Days</span><span className="bt-val big">{s1seData.aggregate.trading_days}</span></div>
                    <div className="bt-summary-card highlight pnl-pos"><span className="bt-label">Winning Days</span><span className="bt-val big">{s1seData.aggregate.winning_days}</span></div>
                    <div className="bt-summary-card highlight pnl-neg"><span className="bt-label">Losing Days</span><span className="bt-val big">{s1seData.aggregate.losing_days}</span></div>
                    <div className="bt-summary-card highlight"><span className="bt-label">Win Rate</span><span className="bt-val big">{s1seData.aggregate.win_rate}%</span></div>
                    <div className="bt-summary-card highlight"><span className="bt-label">Total Trades</span><span className="bt-val big">{s1seData.aggregate.total_trades}</span></div>
                    <div className={`bt-summary-card highlight ${s1seData.aggregate.pnl_per_lot >= 0 ? 'pnl-pos' : 'pnl-neg'}`}><span className="bt-label">Total P&L / Lot</span><span className="bt-val big">{s1seData.aggregate.pnl_per_lot >= 0 ? '+' : ''}{s1seData.aggregate.pnl_per_lot?.toFixed(2)}</span></div>
                    <div className={`bt-summary-card highlight ${s1seData.aggregate.pnl_total >= 0 ? 'pnl-pos' : 'pnl-neg'}`}><span className="bt-label">Total P&L (₹)</span><span className="bt-val big">₹{s1seData.aggregate.pnl_total >= 0 ? '+' : ''}{s1seData.aggregate.pnl_total?.toLocaleString('en-IN')}</span></div>
                    <div className={`bt-summary-card ${s1seData.aggregate.avg_pnl_per_day >= 0 ? 'pnl-pos' : 'pnl-neg'}`}><span className="bt-label">Avg P&L / Day</span><span className="bt-val">{s1seData.aggregate.avg_pnl_per_day >= 0 ? '+' : ''}{s1seData.aggregate.avg_pnl_per_day?.toFixed(2)}</span></div>
                  </div>
                  <div className="bt-day-table"><h3>Daily Breakdown</h3><div className="table-wrapper"><table className="bt-trade-table">
                    <thead><tr><th>Date</th><th>Open</th><th>Close</th><th>Range</th><th>Trades</th><th>Adj</th><th>P&L/Lot</th><th>P&L (₹)</th><th></th></tr></thead>
                    <tbody>
                      {s1seData.days.map((day) => (<React.Fragment key={day.date}>
                        <tr className={`day-row ${day.status !== 'ok' ? 'day-error' : day.summary?.pnl_per_lot >= 0 ? 'day-win' : 'day-loss'}`} onClick={() => setS1seExpandedDay(s1seExpandedDay === day.date ? null : day.date)} style={{ cursor: 'pointer' }}>
                          <td><strong>{day.date}</strong></td>
                          {day.status === 'ok' ? (<><td>{day.summary.nifty_open?.toLocaleString('en-IN')}</td><td>{day.summary.nifty_close?.toLocaleString('en-IN')}</td><td>{day.summary.nifty_range?.toFixed(0)}</td><td>{day.summary.total_trades}</td><td>{day.summary.total_adjustments}</td><td className={day.summary.pnl_per_lot >= 0 ? 'positive' : 'negative'}><strong>{day.summary.pnl_per_lot >= 0 ? '+' : ''}{day.summary.pnl_per_lot?.toFixed(2)}</strong></td><td className={day.summary.pnl_total >= 0 ? 'positive' : 'negative'}>₹{day.summary.pnl_total >= 0 ? '+' : ''}{day.summary.pnl_total?.toLocaleString('en-IN')}</td><td className="expand-icon">{s1seExpandedDay === day.date ? '▼' : '▶'}</td></>) : (<><td colSpan="6" className="day-error-msg">{day.message || 'No data'}</td><td></td></>)}
                        </tr>
                        {s1seExpandedDay === day.date && day.status === 'ok' && (<tr className="expanded-row"><td colSpan="9"><div className="bt-expanded-detail"><table className="bt-trade-table inner-table"><thead><tr><th>#</th><th>Strike</th><th>Entry</th><th>CE Sell ₹</th><th>PE Sell ₹</th><th>Exit</th><th>CE Buy ₹</th><th>PE Buy ₹</th><th>Reason</th><th>CE P&L</th><th>PE P&L</th><th>Total</th></tr></thead><tbody>{day.trades.map((t, i) => (<tr key={i}><td>{t.trade_no}</td><td className="strike-cell">{t.strike}</td><td className="time-cell">{t.entry_time}</td><td>{t.ce_entry?.toFixed(2)}</td><td>{t.pe_entry?.toFixed(2)}</td><td className="time-cell">{t.exit_time}</td><td>{t.ce_exit?.toFixed(2)}</td><td>{t.pe_exit?.toFixed(2)}</td><td className="reason-cell">{t.exit_reason}</td><td className={t.ce_pnl >= 0 ? 'positive' : 'negative'}>{t.ce_pnl >= 0 ? '+' : ''}{t.ce_pnl?.toFixed(2)}</td><td className={t.pe_pnl >= 0 ? 'positive' : 'negative'}>{t.pe_pnl >= 0 ? '+' : ''}{t.pe_pnl?.toFixed(2)}</td><td className={`total-cell ${t.total_pnl >= 0 ? 'positive' : 'negative'}`}>{t.total_pnl >= 0 ? '+' : ''}{t.total_pnl?.toFixed(2)}</td></tr>))}</tbody></table></div></td></tr>)}
                      </React.Fragment>))}
                      {s1seData.days.filter(d => d.status === 'ok').length > 1 && (<tr className="total-row"><td><strong>TOTAL</strong></td><td colSpan="4"></td><td></td><td className={`total-cell ${s1seData.aggregate.pnl_per_lot >= 0 ? 'positive' : 'negative'}`}><strong>{s1seData.aggregate.pnl_per_lot >= 0 ? '+' : ''}{s1seData.aggregate.pnl_per_lot?.toFixed(2)}</strong></td><td className={`total-cell ${s1seData.aggregate.pnl_total >= 0 ? 'positive' : 'negative'}`}><strong>₹{s1seData.aggregate.pnl_total >= 0 ? '+' : ''}{s1seData.aggregate.pnl_total?.toLocaleString('en-IN')}</strong></td><td></td></tr>)}
                    </tbody>
                  </table></div></div>
                </div>
              )}
            </div>

            {!stratData && !btData && (
              <div className="loading">
                <div className="spinner" />
                <p>Loading strategy data...</p>
              </div>
            )}
          </div>
        )}

        {/* ═══════ HISTORICAL TAB ═══════ */}
        {tab === 'historical' && (
          <div className="historical-view">
            {/* Date picker */}
            <section className="hist-controls">
              <div className="hist-date-picker">
                <label>Select Date:</label>
                <input
                  type="date"
                  value={histDate}
                  onChange={(e) => setHistDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                />
                <button className="fetch-btn" onClick={loadHistorical} disabled={histLoading}>
                  {histLoading ? 'Loading...' : 'Fetch Data'}
                </button>
              </div>
            </section>

            {histLoading && (
              <div className="loading">
                <div className="spinner" />
                <p>Fetching data from AngelOne... This may take a few seconds.</p>
              </div>
            )}

            {histError && <div className="error-banner">{histError}</div>}

            {histData && (
              <>
                {/* Day Summary */}
                <section className="price-cards">
                  <PriceCard label="Open" value={histData.summary.open} />
                  <PriceCard label="High" value={histData.summary.high} />
                  <PriceCard label="Low" value={histData.summary.low} />
                  <PriceCard
                    label="Close"
                    value={histData.summary.close}
                    change={histData.summary.change_pct}
                    changeLabel={`% (${histData.summary.change >= 0 ? '+' : ''}${histData.summary.change} pts)`}
                  />
                  <PriceCard label="VIX Open" value={histData.summary.vix_open} />
                  <PriceCard label="VIX Close" value={histData.summary.vix_close} />
                  <div className="price-card">
                    <div className="price-label">Day Range</div>
                    <div className="price-value">{histData.summary.range?.toFixed(0)} pts</div>
                  </div>
                  <div className="price-card">
                    <div className="price-label">Candles</div>
                    <div className="price-value">{histData.summary.total_candles}</div>
                  </div>
                </section>

                {/* Timeline Scrubber */}
                <section className="timeline-section">
                  <h2>Indicator Timeline — {histDate}</h2>
                  <div className="timeline-bar">
                    {histData.snapshots.map((snap, idx) => {
                      const time = snap.candle_time?.split('T')[1]?.substring(0, 5) || '';
                      const isSelected = selectedSnap === snap;
                      const biasColor =
                        snap.overall_bias === 'BULLISH' ? '#22c55e' :
                        snap.overall_bias === 'BEARISH' ? '#ef4444' :
                        snap.overall_bias === 'NO_TRADE' ? '#f59e0b' :
                        snap.overall_bias === 'EXIT_TRADE' ? '#f97316' : '#6b7280';
                      return (
                        <button
                          key={idx}
                          className={`timeline-tick ${isSelected ? 'selected' : ''}`}
                          style={{ borderBottomColor: biasColor, backgroundColor: isSelected ? biasColor + '22' : 'transparent' }}
                          onClick={() => setSelectedSnap(snap)}
                          title={`${time} — ${snap.overall_bias}`}
                        >
                          <span className="tick-time">{time}</span>
                          <span className="tick-dot" style={{ backgroundColor: biasColor }} />
                        </button>
                      );
                    })}
                  </div>
                </section>

                {/* Selected snapshot detail */}
                {selectedSnap && <IndicatorDashboard data={selectedSnap} timeLabel={selectedSnap.candle_time} />}
              </>
            )}
          </div>
        )}

        {/* ═══════ SETTINGS TAB ═══════ */}
        {tab === 'settings' && (
          <div className="settings-view">
            {settingsError && <div className="error-banner">{settingsError}</div>}
            {settingsLoading && <div className="loading"><div className="spinner" /><p>Loading settings...</p></div>}

            {/* Broker Accounts Section */}
            <section className="settings-section">
              <div className="settings-section-header">
                <h2>Broker Accounts</h2>
                <button className="fetch-btn" onClick={() => setShowAddAccount(!showAddAccount)}>
                  {showAddAccount ? 'Cancel' : '+ Add Account'}
                </button>
              </div>

              {showAddAccount && (
                <div className="account-form">
                  <h3>Connect Broker Account</h3>
                  {!connectionResult || connectionResult.status !== 'ok' ? (
                    <>
                      {/* OAuth Login - Primary method (like Sensibull) */}
                      <div className="oauth-login-section">
                        <p className="settings-hint">Login securely via your broker — no credentials stored in this app.</p>
                        <div className="oauth-buttons">
                          <button className="fetch-btn btn-oauth btn-angelone" onClick={handleAngelOneLogin}>
                            🔐 Login with AngelOne
                          </button>
                          <button className="fetch-btn btn-oauth btn-kite" onClick={handleKiteLogin}>
                            🪁 Login with Kite (Zerodha)
                          </button>
                        </div>
                        <p className="oauth-note">You'll be redirected to your broker's login page. After login, you'll be brought back automatically.</p>
                      </div>

                      <div className="login-divider"><span>or connect AngelOne manually</span></div>

                      {/* Manual credential entry - Secondary method */}
                      <p className="settings-hint">Enter your AngelOne SmartAPI credentials to connect. Your account details will be fetched automatically.</p>
                      <div className="form-grid">
                        <div className="form-field">
                          <label>API Key</label>
                          <input type="text" value={newAccount.api_key} onChange={e => setNewAccount({...newAccount, api_key: e.target.value})} placeholder="SmartAPI Key from developer portal" />
                        </div>
                        <div className="form-field">
                          <label>Client ID</label>
                          <input type="text" value={newAccount.client_id} onChange={e => setNewAccount({...newAccount, client_id: e.target.value})} placeholder="e.g. D12345" />
                        </div>
                        <div className="form-field">
                          <label>Password / MPIN</label>
                          <input type="password" value={newAccount.password} onChange={e => setNewAccount({...newAccount, password: e.target.value})} placeholder="Trading password" />
                        </div>
                        <div className="form-field">
                          <label>TOTP Secret</label>
                          <input type="password" value={newAccount.totp_secret} onChange={e => setNewAccount({...newAccount, totp_secret: e.target.value})} placeholder="Base32 secret from authenticator setup" />
                        </div>
                      </div>
                      <div className="account-form-actions">
                        <button className="fetch-btn btn-connect" onClick={handleTestConnection} disabled={testingConnection || !newAccount.api_key || !newAccount.client_id || !newAccount.password || !newAccount.totp_secret}>
                          {testingConnection ? '⏳ Connecting...' : '🔌 Connect to AngelOne'}
                        </button>
                      </div>
                      {connectionResult && connectionResult.status === 'error' && (
                        <div className="connection-result connection-error">
                          ❌ {connectionResult.message}
                        </div>
                      )}
                    </>
                  ) : connectionResult.profile ? (
                    <>
                      <div className="profile-card">
                        <div className="profile-card-header">
                          <span className="profile-avatar">👤</span>
                          <div>
                            <h4>{connectionResult.profile.name || connectionResult.profile.client_id}</h4>
                            <span className="profile-client-id">{connectionResult.profile.client_id}</span>
                          </div>
                          <span className="connection-badge-ok">✓ Connected</span>
                        </div>
                        <div className="profile-details">
                          {connectionResult.profile.email && (
                            <div className="profile-detail-item">
                              <span className="profile-label">Email</span>
                              <span>{connectionResult.profile.email}</span>
                            </div>
                          )}
                          {connectionResult.profile.mobile && (
                            <div className="profile-detail-item">
                              <span className="profile-label">Mobile</span>
                              <span>{connectionResult.profile.mobile}</span>
                            </div>
                          )}
                          {connectionResult.profile.broker && (
                            <div className="profile-detail-item">
                              <span className="profile-label">Broker</span>
                              <span>{connectionResult.profile.broker}</span>
                            </div>
                          )}
                          {connectionResult.profile.exchanges && connectionResult.profile.exchanges.length > 0 && (
                            <div className="profile-detail-item">
                              <span className="profile-label">Exchanges</span>
                              <span>{Array.isArray(connectionResult.profile.exchanges) ? connectionResult.profile.exchanges.join(', ') : connectionResult.profile.exchanges}</span>
                            </div>
                          )}
                          <div className="profile-detail-item">
                            <span className="profile-label">Available Funds</span>
                            <span className="profile-funds">₹{connectionResult.funds?.available_cash !== 'N/A' ? Number(connectionResult.funds.available_cash).toLocaleString('en-IN') : 'N/A'}</span>
                          </div>
                        </div>
                        <div className="profile-card-footer">
                          <label>
                            <input type="checkbox" checked={newAccount.paper_trading} onChange={e => setNewAccount({...newAccount, paper_trading: e.target.checked})} />
                            {' '}Paper Trading Mode
                          </label>
                        </div>
                      </div>
                      <div className="account-form-actions">
                        <button className="fetch-btn btn-connect" onClick={handleSaveAccount}>
                          ✓ Save Account
                        </button>
                        <button className="fetch-btn" onClick={() => setConnectionResult(null)}>
                          ← Back
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* OAuth success message */}
                      <div className="connection-result connection-success" style={{ padding: '16px', textAlign: 'center' }}>
                        <span style={{ fontSize: '24px' }}>✅</span>
                        <p style={{ margin: '8px 0 0' }}>{connectionResult.message}</p>
                      </div>
                      <div className="account-form-actions" style={{ justifyContent: 'center' }}>
                        <button className="fetch-btn" onClick={() => { setConnectionResult(null); setShowAddAccount(false); }}>
                          Done
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {accounts.length > 0 && (
                <div className="table-wrapper">
                  <table className="strat-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Broker</th>
                        <th>Client ID</th>
                        <th>Mode</th>
                        <th>Auth</th>
                        <th>Connection</th>
                        <th>Data Feed</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {accounts.map(acc => (
                        <tr key={acc.id}>
                          <td>{editingAccount?.id === acc.id
                            ? <input type="text" value={editingAccount.name} onChange={e => setEditingAccount({...editingAccount, name: e.target.value})} />
                            : acc.name}</td>
                          <td><span className={`broker-badge broker-${acc.broker || 'angelone'}`}>{acc.broker === 'kite' ? '🪁 Kite' : '📈 AngelOne'}</span></td>
                          <td>{acc.client_id}</td>
                          <td>{acc.paper_trading ? 'Paper' : 'Live'}</td>
                          <td>{acc.login_method === 'oauth' ? '🔐 OAuth' : '🔑 Manual'}</td>
                          <td>
                            <span className={`connection-dot ${acc.is_connected ? 'connected' : 'disconnected'}`}>
                              {acc.is_connected ? '● Connected' : '○ Disconnected'}
                            </span>
                          </td>
                          <td>
                            {acc.is_data_feed ? (
                              <span className="data-feed-badge">📡 Active</span>
                            ) : (
                              <button className="btn-sm" onClick={() => handleSetDataFeed(acc.id)} title="Use this account for live market data">Set as Data Feed</button>
                            )}
                          </td>
                          <td>
                            {editingAccount?.id === acc.id ? (
                              <>
                                <button className="btn-sm" onClick={handleSaveAccount}>Save</button>
                                <button className="btn-sm" onClick={() => setEditingAccount(null)}>Cancel</button>
                              </>
                            ) : (
                              <>
                                <button className="btn-sm" onClick={() => setEditingAccount({...acc})}>Edit</button>
                                <button className="btn-sm btn-danger" onClick={() => handleDeleteAccount(acc.id)}>Delete</button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {!settingsLoading && accounts.length === 0 && (
                <p className="empty-msg">No broker accounts configured. Add one to get started.</p>
              )}
            </section>

            {/* Strategy Instances Section */}
            <section className="settings-section">
              <h2>Strategy Instances</h2>
              <p className="settings-hint">Each strategy can run on multiple accounts with different configurations. Add instances to deploy a strategy across accounts.</p>

              {strategyTypes.map(stype => {
                const instances = strategyConfigs.filter(c => c.strategy_name === stype);
                return (
                  <div key={stype} className="strategy-type-card">
                    <div className="strategy-type-header">
                      <h3>{stype}</h3>
                      <span className="instance-count">{instances.filter(i => i.is_active).length} active</span>
                      <button className="btn-sm btn-primary" onClick={() => setAddingStrategyType(addingStrategyType === stype ? null : stype)}>
                        {addingStrategyType === stype ? 'Cancel' : '+ Add Instance'}
                      </button>
                    </div>

                    {addingStrategyType === stype && (
                      <div className="instance-row instance-editing">
                        <div className="instance-form-grid">
                          <div className="form-field">
                            <label>Account</label>
                            <select value={newInstance.account_id} onChange={e => setNewInstance({...newInstance, account_id: e.target.value ? parseInt(e.target.value) : ''})}>
                              <option value="">Paper Trading</option>
                              {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                            </select>
                          </div>
                          <div className="form-field">
                            <label>Start Time</label>
                            <input type="time" value={newInstance.start_time} onChange={e => setNewInstance({...newInstance, start_time: e.target.value})} />
                          </div>
                          <div className="form-field">
                            <label>Exit Time</label>
                            <input type="time" value={newInstance.exit_time} onChange={e => setNewInstance({...newInstance, exit_time: e.target.value})} />
                          </div>
                          <div className="form-field">
                            <label>Strike Mode</label>
                            <select value={newInstance.strike_mode} onChange={e => setNewInstance({...newInstance, strike_mode: e.target.value})}>
                              <option value="ATM">ATM</option>
                              <option value="ITM">ITM</option>
                            </select>
                          </div>
                          {newInstance.strike_mode === 'ITM' && (
                            <div className="form-field">
                              <label>ITM Points</label>
                              <input type="number" value={newInstance.itm_points} onChange={e => setNewInstance({...newInstance, itm_points: parseInt(e.target.value) || 0})} min="0" step="50" />
                            </div>
                          )}
                          <div className="form-field">
                            <label>Lots</label>
                            <input type="number" value={newInstance.lots} onChange={e => setNewInstance({...newInstance, lots: parseInt(e.target.value) || 1})} min="1" />
                          </div>
                        </div>
                        <div className="instance-actions">
                          <button className="btn-sm btn-primary" onClick={() => handleAddInstance(stype)}>Create</button>
                          <button className="btn-sm" onClick={() => setAddingStrategyType(null)}>Cancel</button>
                        </div>
                      </div>
                    )}

                    {instances.length === 0 && !addingStrategyType && (
                      <p className="empty-msg">No instances configured for this strategy.</p>
                    )}

                    {instances.map(inst => (
                      <StrategyInstance key={inst.id} instance={inst} accounts={accounts} onSave={handleUpdateInstance} onDelete={handleDeleteInstance} />
                    ))}
                  </div>
                );
              })}

              {!settingsLoading && strategyTypes.length === 0 && (
                <p className="empty-msg">No strategy types available.</p>
              )}
            </section>
          </div>
        )}
      </main>

      <footer className="footer">
        <span>OptionSelling v1.0</span>
        <span>Auto-refresh: {REFRESH_INTERVAL / 1000}s</span>
        {systemStatus?.error && (
          <span className="footer-error">Error: {systemStatus.error}</span>
        )}
      </footer>
    </div>
  );
}

export default App;
